import {ActivityPanelPortal, WebActivityPanelLayout} from './activity-panel-layout'
import {ConnectionsPanel} from './connections-panel'
import {PanelHeader} from './panel-header'
import {groupDocumentConnections} from '@shm/shared/connections'
import {useQueryClient} from '@tanstack/react-query'
import {commentThreadSelection, getCommentThread} from '@shm/shared/comments'
import {queryDocumentComments} from '@shm/shared/models/queries'
import type {EditorBlock, EditorQueryBlock} from '@seed-hypermedia/client/editor-types'
import {AgentDocumentView} from './agent-document-view'
import {
  BlockRange,
  countCustomMetadataFields,
  HMBlockNode,
  HMBlockQuery,
  HMComment,
  HMDocument,
  HMExistingDraft,
  HMListedDraft,
  HMMetadata,
  HMQueryTableConfig,
  HMQueryBlockInput,
  HMRawCitation,
  HMResource,
  normalizeQuerySort,
  UnpackedHypermediaId,
} from '@seed-hypermedia/client/hm-types'
import {
  createInspectNavRoute,
  DocumentPanelRoute,
  extractRefs,
  findContentBlock,
  getBlockText,
  getDraftMentionRefs,
  getDraftNodesOutline,
  getMetadataName,
  getNodesOutline,
  hmId,
  NavRoute,
  ProfileTab,
  packHmId,
  replaceRouteDocumentId,
  routeToHref,
  unpackHmId,
  useUniversalAppContext,
} from '@shm/shared'
import {
  CommentsProvider,
  CommentDraftReferenceProvider,
  useCommentDraftReferences,
  isRouteEqualToCommentTarget,
  useCommentsServiceContext,
  useHackyAuthorsSubscriptions,
} from '@shm/shared/comments-service-provider'
import {IS_DESKTOP, NOTIFY_SERVICE_HOST} from '@shm/shared/constants'
import type {
  BlockRangeSelectOptions,
  CitationFragmentClick,
  CitationFragmentHighlight,
  DocumentContentProps,
  LinkExtensionOptions,
} from '@shm/shared/document-content-props'
import {CONTENT_IMAGE_POLICY, COVER_IMAGE_POLICY, processImage, processImageIfSupported} from './image-processing'
import {findDraftForPath, isDraftPlaceholderPath, useDraftsForAccountSafe} from '@shm/shared/draft-breadcrumb-context'
import {parseExploreQuery, type HMExploreResultType} from '@shm/shared/explore'
import {useIsHomeDraftOverride} from '@shm/shared/home-draft-context'
import type {DocumentMachineEvent, TransientResourceError} from '@shm/shared/models/document-machine'
import {
  useAccount,
  useAccountsMetadata,
  useCitations,
  useDirectory,
  useDocumentCollaborators,
  useIsLatest,
  useResource,
  useResources,
  useSiteMembers,
} from '@shm/shared/models/entity'
import {useMentionNames} from '@shm/shared/models/inline-mentions'
import {blockToPlainText} from '@shm/shared/content-to-text'
import {useExploreResults} from '@shm/shared/models/explore'
import {useInteractionSummary} from '@shm/shared/models/interaction-summary'
import {queryQueryBlock} from '@shm/shared/models/queries'
import {sortTermToDocumentSort} from '@shm/shared/models/query-block-sort'
import {QueryBlockDraftSlotData, useQueryBlockDrafts} from '@shm/shared/query-block-drafts-context'
import {useDebounce} from '@shm/shared/utils/use-debounce'
import {
  documentMachine,
  DocumentMachineProvider,
  selectCanEdit,
  selectCanEditCurrentRoute,
  selectCollectionQueryBlock,
  selectContext,
  selectDraftOverlayAllowed,
  selectIsCollection,
  selectIsEditing,
  selectIsUnpublishedDraft,
  selectPublishedVersion,
  selectRenderableBlocks,
  selectShouldFocusDraftTitle,
  selectShouldUseDraftOverlay,
  useAccountSync,
  useAutoRebase,
  useCapabilitySync,
  useDocumentNavigationOptional,
  useDocumentSelector,
  useDocumentSend,
  useDocumentSync,
  useDraftResolutionSync,
  useOldVersionEditBlocked,
  useResourceTransientSync,
  useScrollSync,
  useVersionLatestSync,
} from '@shm/shared/models/use-document-machine'
import {useEditorGate} from '@shm/shared/models/use-editor-gate'
import {getExpandedDocumentPanel, getRoutePanel} from '@shm/shared/routes'
import {ActivityContent, ActivityHeader} from './activity-panel'
import {useOpenUrl, useUniversalClient} from '@shm/shared/routing'
import {DocumentPageView, documentPageCid} from './document-page'
import {usePublishError} from './editing-toolbar'
import {getBreadcrumbDocumentIds, isDraftPathSegment} from '@shm/shared/utils/breadcrumbs'
import {
  activitySlugToFilter,
  activityFilterToSlug,
  getCommentTargetId,
  getVersionHeads,
  hmIdToURL,
  latestId,
  parseFragment,
  routeToUrl,
} from '@shm/shared/utils/entity-id-url'
import {useNavigate, useNavRoute} from '@shm/shared/utils/navigation'
import {isPendingSpaceUid} from '@shm/shared/utils/pending-space'
import {getReservedLazyDraftBreadcrumbName} from '@shm/shared/utils/reserved-draft-ids'
import {useIsomorphicLayoutEffect} from '@shm/shared/utils/use-isomorphic-layout-effect'
import {useQuery} from '@tanstack/react-query'
import {
  ArrowUp,
  Braces,
  FilePen,
  FileText,
  Grid3X3,
  Info,
  MessagesSquare,
  Minimize2,
  ShieldCheck,
  SlidersHorizontal,
  Users,
  LayoutGrid,
  List as ListIcon,
  MoreHorizontal,
  Pencil,
  Plus,
  Quote,
  Search,
  Table as TableIcon,
  Trash,
} from 'lucide-react'
import {lazy, ReactNode, Suspense, useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {AccountPage} from './account-page'
import {AllDocumentsPage} from './all-documents-page'
import {Button} from './button'
import {ChildDeletionPublishDialog} from './child-deletion-publish-dialog'
import {CollaboratorsPage, getRenderedCollaboratorsCount} from './collaborators-page'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './components/alert-dialog'
import {Popover, PopoverAnchor, PopoverContent, PopoverTrigger} from './components/popover'
import {ScrollArea} from './components/scroll-area'
import {Tabs, TabsList, TabsTrigger} from './components/tabs'
import {DirectoryPageContent} from './directory-page'
import {CommentsFooterContext, DiscussionsPageContent} from './discussions-page'
import {DocumentCover} from './document-cover'
import {AuthorPayload, BreadcrumbEntry, DocumentHeader} from './document-header'
import {
  DocumentMetadataAffordanceButtons,
  EditableDocumentMetadataFields,
  HomeDocumentMetadataAffordanceBar,
} from './document-metadata-affordances'
import {DocumentMetadataView} from './document-metadata-view'
import {RequiredAttributesEditor} from './required-attributes-editor'
import {schemaDefinitionCid, SchemaDocumentHeaderActions, useSchemaMenuItems} from './schema/schema-document'
import {HMEntityField, HMEntityLink} from './hm-entity-field'
import {emptyStructSchema, SchemaEditor} from './schema/schema-editor'
import {SizableText, Text} from './text'
import {SchemaBrowserPage} from './schema/schema-browser'
import {
  classifyRef,
  useConformanceSchema,
  useEffectiveDocSchema,
  useParentDraftChildAttributesSchema,
  useResolvedSchema,
} from './schema/schema-resolve'
import {BINDING_SCHEMA_KEYS, type BindingSchemaKey} from '@shm/shared/models/schema-draft'
import {Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle} from './components/dialog'
import {DocumentTools} from './document-tools'
import {nameForCid, nameToUrl, HM_SCHEMAS} from './schema/engine'
import {libraryPageUrl} from './schema/schema-browser'
import {DepLists, SchemaNavContext, SchemaView} from './schema/explorer'
import {useSchemaRegistry} from './schema/schema-registry-cid'
import {DocumentTopBar} from './document-top-bar'
import {DocumentVersionsPanel, isDocumentVersionsPanelRoute} from './document-versions-panel'
import {ExplorePage} from './explore-page'
import {Feed, type DraftVersionEntry} from './feed'
import {FeedFilters} from './feed-filters'
import {SelectField, SwitchField, TextField} from './form-fields'
import {HMIcon} from './hm-icon'
import {useDocumentLayout} from './layout'
import {MembersFacepile} from './members-facepile'
import {MergedBadge} from './merged-badge'
import {MobilePanelSheet} from './mobile-panel-sheet'
import {DocNavigationItem, DocNavigationWrapper, DocumentOutline, isValidSiteHeaderItem} from './navigation'
import {MenuItemType, OptionsDropdown} from './options-dropdown'
import {OptionsPanel} from './options-panel'
import {PageLayout} from './page-layout'
import {PageDeleted, PageDiscovery, PageNotFound, PagePrivate} from './page-message-states'
import {PanelLayout} from './panel-layout'
import {PrivateBadge} from './private-badge'
import {QueryBlockContent} from './query-block-content'
import {buildSlotItems} from './query-block-draft-items'
import {SiteFileBrowserLayout} from './site-file-browser-layout'
import {SiteHeader} from './site-header'
import {Spinner} from './spinner'
import {Tooltip} from './tooltip'
import {toast} from './toast'
import {useBlockScroll} from './use-block-scroll'
import {useCopyHmLink} from './use-copy-hm-link'
import {useMedia} from './use-media'
import {cn} from './utils'
import {AttributeAutocomplete, AttributeAutocompleteProvider} from './value-editor'

/** Build a draft breadcrumb while preserving published metadata omitted by partial draft edits. */
export function getDraftBreadcrumbEntry({
  id,
  draft,
  publishedMetadata,
  currentMetadata,
  isUnpublishedDraft,
  isLoading,
}: {
  id: UnpackedHypermediaId
  draft: HMListedDraft
  publishedMetadata?: HMMetadata
  currentMetadata?: HMMetadata
  isUnpublishedDraft: boolean
  isLoading: boolean
}): BreadcrumbEntry {
  const metadata = {...publishedMetadata, ...draft.metadata, ...currentMetadata}
  return {
    id,
    metadata,
    draftId: draft.id,
    fallbackName: isUnpublishedDraft
      ? draft.visibility === 'PRIVATE'
        ? 'New Private Document'
        : 'New Document'
      : 'Untitled Document',
    isLoading: !metadata.name && isLoading,
    isTombstone: false,
    isNotFound: false,
    isError: false,
    isUnpublishedDraft,
  }
}

/** Creates a right-panel route showing citations to one block. */
export function getBlockCitationsPanelRoute(
  docId: UnpackedHypermediaId,
  targetBlockId: string,
): Extract<DocumentPanelRoute, {key: 'activity'}> {
  return {
    key: 'activity',
    id: docId,
    filterEventType: activitySlugToFilter('citations'),
    targetBlockId,
  }
}

const LazyDocumentMachineDebugDrawer = lazy(() =>
  import('@shm/shared/models/document-machine-debug-drawer').then((m) => ({default: m.DocumentMachineDebugDrawer})),
)

function sourceTypeKind(sourceType?: string): CitationFragmentHighlight['sourceType'] {
  const normalized = sourceType?.toLowerCase() ?? ''
  if (normalized.startsWith('doc/') || normalized === 'ref') return 'document'
  if (normalized.startsWith('comment/') || normalized === 'comment') return 'comment'
  return 'unknown'
}

function sourceCommentId(source?: string): string | null {
  if (!source) return null
  if (source.startsWith('hm://')) return source.slice('hm://'.length)
  const unpacked = unpackHmId(source)
  if (!unpacked) return source || null
  return [unpacked.uid, ...(unpacked.path ?? [])].join('/')
}

function normalizeCitationFragmentHighlights(citations: HMRawCitation[] | undefined): CitationFragmentHighlight[] {
  return (citations ?? []).flatMap((citation, index) => {
    const fragment = parseFragment(citation.targetFragment ?? null)
    if (!fragment?.blockId || fragment.start == null || fragment.end == null || fragment.end <= fragment.start) {
      return []
    }

    const kind = sourceTypeKind(citation.sourceType)
    const sourceId = unpackHmId(citation.source) ?? null
    const sourceDocumentId =
      kind === 'comment' ? unpackHmId(citation.sourceDocument || citation.source) ?? sourceId : sourceId
    const sourceBlockId = citation.sourceContext?.trim() || null

    return [
      {
        id: `${citation.source || 'unknown'}:${citation.targetFragment || ''}:${citation.sourceContext || ''}:${index}`,
        targetBlockId: fragment.blockId,
        targetRange: {start: fragment.start, end: fragment.end},
        sourceType: kind,
        sourceId:
          kind === 'document' && sourceId
            ? {
                ...sourceId,
                version: sourceId.version,
                blockRef: sourceBlockId,
                blockRange: sourceBlockId ? {expanded: true} : null,
              }
            : sourceId,
        sourceDocumentId,
        sourceBlockId,
        sourceCommentId: kind === 'comment' ? sourceCommentId(citation.source) : null,
        sourceAuthorUid: citation.sourceBlob?.author || sourceId?.uid || null,
        targetBlockRevision: citation.targetBlockRevision || null,
        raw: citation,
      },
    ]
  })
}

function blockNodeText(node: HMBlockNode): string {
  const ownText = node.block ? getBlockText(node.block) : ''
  const childText = (node.children ?? []).map(blockNodeText).join(' ')
  return `${ownText} ${childText}`.trim()
}

function commentPreviewText(comment: HMComment | undefined): string {
  if (!comment) return 'Loading comment…'
  const text = comment.content.map(blockNodeText).join(' ').replace(/\s+/g, ' ').trim()
  if (!text) return 'Comment'
  if (text.length <= 50) return text
  return `${text.slice(0, 50).trimEnd()}…`
}

function documentCitationTitle(citation: CitationFragmentHighlight, resource?: ReturnType<typeof useResource>['data']) {
  if (resource?.type === 'document')
    return resource.document?.metadata?.name || citation.sourceId?.path?.join('/') || 'Untitled document'
  return citation.sourceId?.path?.join('/') || citation.sourceId?.uid || 'Document citation'
}

function CitationFragmentPopover({
  click,
  onClose,
  onCitationSelect,
}: {
  click: CitationFragmentClick
  onClose: () => void
  onCitationSelect: (citation: CitationFragmentHighlight) => void
}) {
  const {universalClient} = useUniversalAppContext()
  const documentCitations = click.citations.filter(
    (citation) => citation.sourceType === 'document' && citation.sourceId,
  )
  const documentResources = useResources(
    documentCitations.map((citation) => citation.sourceId),
    {enabled: documentCitations.length > 0},
  )
  const sourceDocumentIds = Array.from(
    new Map(
      click.citations
        .filter((citation) => citation.sourceType === 'comment' && citation.sourceDocumentId)
        .map((citation) => [citation.sourceDocumentId!.id, citation.sourceDocumentId!]),
    ).values(),
  )
  const commentsQuery = useQuery({
    queryKey: ['citation-fragment-comment-previews', sourceDocumentIds.map((id) => id.id).join('|')],
    queryFn: async () => {
      const entries = await Promise.all(
        sourceDocumentIds.map(async (id) => {
          const result = await universalClient.request('ListComments', {targetId: id})
          return [id.id, result] as const
        }),
      )
      return Object.fromEntries(entries)
    },
    enabled: sourceDocumentIds.length > 0,
  })
  const accountMetadata = useAccountsMetadata(
    Array.from(
      new Set(click.citations.map((citation) => citation.sourceAuthorUid).filter((uid): uid is string => !!uid)),
    ),
  )
  const documentResourceByCitation = new Map(
    documentCitations.map((citation, index) => [citation.id, documentResources[index]?.data]),
  )

  return (
    <Popover open onOpenChange={(open) => !open && onClose()}>
      <PopoverAnchor asChild>
        <span
          className="pointer-events-none fixed z-50 size-px"
          style={{left: click.clientX, top: click.clientY}}
          aria-hidden="true"
        />
      </PopoverAnchor>
      <PopoverContent align="start" side="bottom" className="w-80 p-2">
        <div className="flex flex-col gap-1">
          <div className="text-muted-foreground px-2 py-1 text-xs font-medium">
            Cited {click.citations.length === 1 ? 'here' : `${click.citations.length} times here`}
          </div>
          {click.citations.map((citation) => {
            const commentData = citation.sourceDocumentId
              ? commentsQuery.data?.[citation.sourceDocumentId.id]
              : undefined
            const comment = commentData?.comments?.find((item: HMComment) => item.id === citation.sourceCommentId)
            const authorUid = comment?.author || citation.sourceAuthorUid || undefined
            const author = authorUid ? commentData?.authors?.[authorUid] || accountMetadata.data[authorUid] : null
            return (
              <button
                key={citation.id}
                type="button"
                className="hover:bg-accent flex w-full items-start gap-2 rounded-md px-2 py-2 text-left"
                onClick={() => onCitationSelect(citation)}
              >
                {citation.sourceType === 'comment' ? (
                  <>
                    {authorUid ? (
                      <HMIcon
                        id={hmId(authorUid)}
                        name={author?.metadata?.name}
                        icon={author?.metadata?.icon}
                        size={24}
                      />
                    ) : (
                      <div className="bg-muted size-6 shrink-0 rounded-full" />
                    )}
                    <div className="min-w-0 flex-1">
                      <div className="text-muted-foreground truncate text-xs">
                        {author?.metadata?.name || authorUid || 'Comment'}
                      </div>
                      <div className="line-clamp-2 text-sm">{commentPreviewText(comment)}</div>
                    </div>
                  </>
                ) : (
                  <>
                    <Quote className="text-muted-foreground mt-0.5 size-4 shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-medium">
                        {documentCitationTitle(citation, documentResourceByCitation.get(citation.id))}
                      </div>
                      {citation.sourceBlockId ? (
                        <div className="text-muted-foreground truncate text-xs">Block {citation.sourceBlockId}</div>
                      ) : null}
                    </div>
                  </>
                )}
              </button>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}

/** Document tabs remain available as panels while the page owns the main view. */
export function getDocumentPagePanelMenuItems(
  id: UnpackedHypermediaId,
  openPanel: (panel: DocumentPanelRoute) => void,
): MenuItemType[] {
  const target = {...id, blockRef: null, blockRange: null}
  return [
    {
      key: 'app-people',
      label: 'People',
      icon: <Users className="size-4" />,
      onClick: () => openPanel({key: 'collaborators', id: target}),
    },
    {
      key: 'app-comments',
      label: 'Comments',
      icon: <MessagesSquare className="size-4" />,
      onClick: () => openPanel({key: 'comments', id: target}),
    },
    {
      key: 'app-citations',
      label: 'Citations',
      icon: <Quote className="size-4" />,
      onClick: () => openPanel({key: 'activity', id: target, filterEventType: activitySlugToFilter('citations')}),
    },
    {
      key: 'app-attributes',
      label: 'Attributes',
      icon: <Info className="size-4" />,
      onClick: () => openPanel({key: 'metadata', id: target}),
    },
  ]
}

/** Removes redundant document destinations and orders the remaining menu actions by intent. */
export function orderDocumentMenuItems(items: MenuItemType[]): MenuItemType[] {
  const hiddenKeys = new Set(['metadata', 'directory', 'all-documents'])
  const visibleItems = items.filter((item) => !hiddenKeys.has(item.key))
  const itemOrder = [
    'page-parameters',
    'page-permissions',
    'app-people',
    'app-comments',
    'app-citations',
    'app-attributes',
    'app-revoke',
    'new',
    'add-raw-field',
    'options',
    'versions',
    'convert-to-collection',
    'convert-to-document',
    'copy-link',
    'link-site',
    'link',
    'publish-site',
    'publish-custom-domain',
    'email-subscribers',
    'move',
    'duplicate',
    'branch',
    'republish',
    'export',
    'citation-fragments-toggle',
    'inspect',
  ]
  const byKey = new Map(visibleItems.map((item) => [item.key, item]))
  const orderedItems: MenuItemType[] = []
  const consumed = new Set<string>()

  for (const key of itemOrder) {
    const item = byKey.get(key)
    if (item && item.variant !== 'destructive') {
      orderedItems.push(item)
      consumed.add(key)
    }
  }
  for (const item of visibleItems) {
    if (!consumed.has(item.key) && item.variant !== 'destructive') orderedItems.push(item)
  }
  for (const item of visibleItems) {
    if (item.variant === 'destructive') orderedItems.push(item)
  }

  return orderedItems
}

/** Returns a stable key for the exact document resource being viewed, including version state. */
export function getDocumentResourceRouteKey(id: UnpackedHypermediaId): string {
  return `${id.id}@${id.version ?? ''}@${id.latest ? 'latest' : ''}`
}

/** Returns true when the route should warn that it is pinned to an older document version. */
export function shouldShowOlderVersionToast({
  routeDocId,
  latestVersion,
}: {
  routeDocId: UnpackedHypermediaId
  latestVersion?: string | null
}): boolean {
  return !!routeDocId.version && !!latestVersion && routeDocId.version !== latestVersion
}

/** Returns the stable toast ID for a document's older-version warning. */
export function getOlderVersionToastId(docId: UnpackedHypermediaId): string {
  return `older-version-linked:${docId.id}`
}

/** Returns the toast options for a blocked old-version edit attempt. */
export function getOldVersionEditBlockedToastOptions(docId: UnpackedHypermediaId) {
  return {
    id: `old-version-edit-blocked:${docId.id}`,
    description: 'Go to the latest version to make changes.',
    duration: 3000,
    position: 'bottom-center' as const,
  }
}

/** Returns the current route pointed at the latest document version while preserving route UI state. */
export function getLatestRouteForCurrentDocumentRoute(route: NavRoute): NavRoute {
  if (!('id' in route) || typeof route.id !== 'object' || route.id === null) return route

  return {
    ...route,
    id: {
      ...route.id,
      version: null,
      latest: true,
    },
  } as NavRoute
}

export type ActiveView =
  | 'content'
  | 'activity'
  | 'comments'
  | 'directory'
  | 'collaborators'
  | 'site-profile'
  | 'all-documents'
  | 'explore'
  | 'metadata'
  | 'schema'

/** Returns the document and focused comment rendered by a comments panel. */
export function getCommentsPanelTarget(
  docId: UnpackedHypermediaId,
  panelRoute: DocumentPanelRoute | null,
): {docId: UnpackedHypermediaId; openComment?: string} {
  if (panelRoute?.key !== 'comments') return {docId, openComment: undefined}

  const panelDocId = panelRoute.id ?? docId
  return {
    docId:
      panelDocId.id === docId.id && !panelDocId.version && docId.version
        ? {...panelDocId, version: docId.version}
        : panelDocId,
    openComment: panelRoute.openComment,
  }
}

/** Selects the action controls shown for document content. */
export function getDocumentContentAction({
  activeView,
  isEditing,
  hasDraft,
  editingFloatingActions,
  draftActions,
  actionButtons,
  allMenuItems,
}: {
  activeView: ActiveView
  isEditing: boolean
  hasDraft: boolean
  editingFloatingActions?: (props: {menuItems: MenuItemType[]}) => ReactNode
  draftActions?: (props: {menuItems: MenuItemType[]}) => ReactNode
  actionButtons: ReactNode
  allMenuItems: MenuItemType[]
}) {
  // The Schema tab is read-only, but its actions (Extend Schema, New <Type>, Inspect via dev
  // menu) live in the regular options dropdown — so the plain action buttons must render there,
  // without the editing/draft chrome.
  if (activeView === 'schema') return actionButtons
  if (activeView !== 'content' && activeView !== 'metadata') return null
  if (editingFloatingActions) return editingFloatingActions({menuItems: allMenuItems})
  if (!isEditing && hasDraft && draftActions) return draftActions({menuItems: allMenuItems})
  return actionButtons
}

type CommentDraftTarget = {
  docId: string
  commentId?: string
  quotingBlockId?: string
  quotingRangeStart?: number
  quotingRangeEnd?: number
}

/** Returns the document ID that should back the rendered document content. */
export function getRenderedDocumentId(
  routeDocId: UnpackedHypermediaId,
  resourceData: HMResource | null | undefined,
  resourceFetchId: UnpackedHypermediaId | null | undefined = routeDocId,
) {
  if (!resourceFetchId) return routeDocId
  return resourceData?.type === 'document' ? resourceData.id : routeDocId
}

/**
 * React key for `DocumentMachineProvider`. Recreates the machine ONLY when the
 * bound document identity changes — i.e. the uid+path (`renderedDocId.id`,
 * version-independent), which changes on a first-publish slug. It deliberately
 * does NOT depend on the version: publishing bumps the resolved version and the
 * content route resets `?v=` to latest, both of which would otherwise recreate
 * the actor mid/post-publish and re-load the just-cleared draft, stranding the
 * machine in `editing` (Publish button stuck green). Version changes are handled
 * in-place via `version.changed` / `document.remoteUpdate` events, not a remount.
 */
export function getDocumentMachineKey(renderedDocId: UnpackedHypermediaId): string {
  return renderedDocId.id
}

function extractQuotingRange(blockRange?: BlockRange | null): {start: number; end: number} | undefined {
  if (!blockRange) return undefined
  if (
    'start' in blockRange &&
    'end' in blockRange &&
    typeof blockRange.start === 'number' &&
    typeof blockRange.end === 'number'
  ) {
    return {start: blockRange.start, end: blockRange.end}
  }
  return undefined
}

function getCommentEditorRouteKey(params?: {
  openComment?: string
  targetBlockId?: string
  blockRange?: BlockRange | null
}) {
  const range = extractQuotingRange(params?.blockRange ?? null)
  return [params?.openComment ?? 'new', params?.targetBlockId ?? 'document', range?.start ?? '', range?.end ?? ''].join(
    ':',
  )
}

function getCommentDraftTarget(
  docId: UnpackedHypermediaId,
  params?: {openComment?: string; targetBlockId?: string; blockRange?: BlockRange | null},
): CommentDraftTarget {
  const range = extractQuotingRange(params?.blockRange ?? null)
  return {
    docId: docId.id,
    commentId: params?.openComment,
    quotingBlockId: params?.targetBlockId,
    quotingRangeStart: range?.start,
    quotingRangeEnd: range?.end,
  }
}

function areSameCommentDraftTarget(a: CommentDraftTarget, b: CommentDraftTarget) {
  return (
    a.docId === b.docId &&
    a.commentId === b.commentId &&
    a.quotingBlockId === b.quotingBlockId &&
    a.quotingRangeStart === b.quotingRangeStart &&
    a.quotingRangeEnd === b.quotingRangeEnd
  )
}

export function shouldSuppressMainCommentEditor({
  docId,
  activeView,
  discussionsParams,
  panelRoute,
}: {
  docId: UnpackedHypermediaId
  activeView: ActiveView
  discussionsParams?: {openComment?: string; targetBlockId?: string; blockRange?: BlockRange | null}
  panelRoute: DocumentPanelRoute | null
}) {
  if (activeView !== 'comments' || panelRoute?.key !== 'comments') return false

  return areSameCommentDraftTarget(
    getCommentDraftTarget(docId, discussionsParams),
    getCommentDraftTarget(panelRoute.id, {
      openComment: panelRoute.openComment,
      targetBlockId: panelRoute.targetBlockId,
      blockRange: panelRoute.blockRange,
    }),
  )
}

export function shouldUseDraftForRenderedDocument({
  docId,
  existingDraft,
  isLatest,
}: {
  docId: UnpackedHypermediaId
  existingDraft?: HMExistingDraft | false
  isLatest?: boolean
}) {
  if (!existingDraft) return false
  // True old-version snapshots are immutable. If a link includes the latest
  // version CID, treat it like the normal latest route so writers keep access
  // to their draft and edit affordances.
  if (docId.version && !isLatest) return false
  return true
}

/**
 * Collapse the resolved-draft state into what gets handed to the document
 * machine. `undefined` means "draft resolution is still loading" and MUST be
 * preserved end-to-end: the machine's one-shot `draft.resolved` only fires once,
 * so prematurely collapsing `undefined` to `false` (no draft) latches it to the
 * no-draft branch and strands a saved draft that finishes loading a moment later.
 * Only once `existingDraft` has genuinely settled do we apply the
 * `shouldUseDraft` gate (which is intentionally false while `existingDraft` is
 * still `undefined`, since `!undefined` is truthy).
 */
export function resolveEffectiveExistingDraft(
  existingDraft: HMExistingDraft | false | undefined,
  shouldUseDraft: boolean,
): HMExistingDraft | false | undefined {
  if (existingDraft === undefined) return undefined
  return shouldUseDraft ? existingDraft : false
}

/** Return true when a local draft should bypass remote resource loading states. */
export function hasUnpublishedDraftForResourceState({
  existingDraft,
  reservedDraftId,
  resourceFetchId,
  resourceIsDiscovering,
  resourceData,
}: {
  existingDraft?: HMExistingDraft | false
  reservedDraftId?: string | null
  resourceFetchId: UnpackedHypermediaId | null
  resourceIsDiscovering: boolean
  resourceData?: {type?: string; message?: string} | null
}) {
  const hasVirtualDraft = !!reservedDraftId && !existingDraft
  return (
    (!!existingDraft || hasVirtualDraft) &&
    (resourceFetchId === null ||
      resourceIsDiscovering ||
      !resourceData ||
      resourceData.type === 'not-found' ||
      (resourceData.type === 'error' && !resourceData.message?.toLowerCase?.().includes('permission')))
  )
}

export function getEffectiveCanEdit({
  canEdit,
  resourceFetchId,
  existingDraft,
  reservedDraftId,
}: {
  canEdit: boolean
  resourceFetchId: UnpackedHypermediaId | null
  existingDraft?: HMExistingDraft | false
  reservedDraftId?: string | null
}) {
  return canEdit || (resourceFetchId === null && (!!existingDraft || !!reservedDraftId))
}

export function getDocumentSyncIsPlaceholderData({
  resourceFetchId,
  hasUnpublishedDraft,
  resourceIsPreviousData,
}: {
  resourceFetchId: UnpackedHypermediaId | null
  hasUnpublishedDraft: boolean
  resourceIsPreviousData?: boolean
}) {
  return !!resourceIsPreviousData && resourceFetchId !== null && !hasUnpublishedDraft
}

/** Returns the published document ID to use for citation queries, or null when citations cannot exist yet. */
export function getCitationsTargetId({
  docId,
  documentVersion,
}: {
  docId: UnpackedHypermediaId
  documentVersion?: string | null
}) {
  if (!documentVersion || isPendingSpaceUid(docId.uid)) return null
  return {...docId, blockRef: null, blockRange: null}
}

/** Appends a pinned block reference to the active draft without moving the document. */
export function getBlockCommentDraftReference({
  route,
  sourceId,
  resolvedThreadRootId,
  version,
  blockId,
  blockRange,
}: {
  route: Extract<NavRoute, {key: 'document' | 'feed'}>
  sourceId?: UnpackedHypermediaId
  resolvedThreadRootId?: string
  version: string
  blockId: string
  blockRange?: BlockRange
}) {
  const commentsPanel = route.panel?.key === 'comments' ? route.panel : undefined
  return {
    reference: {
      documentId: (commentsPanel?.id ?? route.id).id,
      commentId: resolvedThreadRootId || commentsPanel?.openComment,
      link: packHmId({
        ...(sourceId ?? route.id),
        version,
        latest: null,
        blockRef: blockId,
        blockRange: blockRange && 'start' in blockRange && 'end' in blockRange ? blockRange : null,
      }),
    },
    route: commentsPanel ? route : {...route, panel: {key: 'comments' as const, id: route.id}},
  }
}

/** Opens the root thread and retains the selected reply for display. */
export function getCommentReplyPanelRoute({
  docId,
  comment,
  isReplying = false,
}: {
  docId: UnpackedHypermediaId
  comment: HMComment
  isReplying?: boolean
}): Extract<DocumentPanelRoute, {key: 'comments'}> {
  const targetRoute = isRouteEqualToCommentTarget({
    id: docId,
    comment,
  })
  const replyVersionData = isReplying
    ? {
        isReplying: true,
        replyCommentVersion: comment.version,
        rootReplyCommentVersion: comment.threadRootVersion || comment.version,
      }
    : {}

  return {
    key: 'comments',
    id: targetRoute || docId,
    ...commentThreadSelection(comment),
    ...replyVersionData,
  }
}

function getActiveView(routeKey: string): ActiveView {
  switch (routeKey) {
    case 'activity':
      return 'activity'
    case 'comments':
      return 'comments'
    case 'directory':
      return 'directory'
    case 'collaborators':
      return 'collaborators'
    case 'all-documents':
      return 'all-documents'
    case 'explore':
      return 'explore'
    case 'site-profile':
      return 'site-profile'
    case 'metadata':
      return 'metadata'
    case 'schema':
      return 'schema'
    default:
      return 'content'
  }
}

export interface CommentEditorProps {
  /** Use a compact field with a separate send button in the panel footer. */
  compact?: boolean
  /** Called after the comment is published successfully. */
  onCommentSubmitted?: (comment: HMComment) => void
  docId: UnpackedHypermediaId
  quotingBlockId?: string
  /** Codepoint range within the quoted block. Absent ⇒ whole-block quote. */
  quotingRange?: {start: number; end: number}
  commentId?: string
  isReplying?: boolean
  /** Focus the editor on mount. Renamed from `autoFocus` to avoid `jsx-a11y/no-autofocus`; focus driven imperatively. */
  focusOnMount?: boolean
  /** CID version of the comment being replied to. */
  replyCommentVersion?: string
  /** CID version of the thread root comment. */
  rootReplyCommentVersion?: string
}

export interface ResourcePageProps {
  docId: UnpackedHypermediaId
  /** Resource ID to fetch for published content. Pass null for local-only draft routes that must not hit the backend. */
  resourceId?: UnpackedHypermediaId | null
  /** Factory to create comment editor - platform-specific (web vs desktop) */
  CommentEditor?: React.ComponentType<CommentEditorProps>
  /** Complete platform-specific menu items for the options dropdown */
  optionsMenuItems?: MenuItemType[]
  /** Root-level creation menu shown beside Home in the file explorer. */
  fileBrowserCreateMenuItem?: MenuItemType | null
  fileBrowserOnIncludeDocument?: import('./site-file-browser').SiteFileBrowserProps['onIncludeDocument']
  fileBrowserGetIncludeDocumentState?: import('./site-file-browser').SiteFileBrowserProps['getIncludeDocumentState']
  /** Platform handler for "Extend Schema": opens a flow that creates an extending document draft.
   * Absent (web), extend falls back to the raw blob-draft route. */
  onExtendSchema?: (baseSchemaCid: string) => void
  /** Platform handler for "New Document" on a schema page: a draft whose `attributesSchema` is the page. */
  onNewTypedDocument?: (schemaUrl: string) => void
  /** Platform handler for "New Collection" on a schema page: a draft whose `childAttributesSchema` is the page. */
  onNewTypedCollection?: (schemaUrl: string) => void
  /** @deprecated use optionsMenuItems */
  extraMenuItems?: MenuItemType[]
  /** Existing draft info for showing draft indicator in toolbar */
  existingDraft?: HMExistingDraft | false
  /** Route-reserved draft id for an empty draft that has not been persisted yet. */
  reservedDraftId?: string | null
  /** Visibility of the existing draft, when the platform can provide full draft data. */
  existingDraftVisibility?: HMDocument['visibility']
  /** Pre-fetched content blocks from the existing draft (when available, used as editor initial content) */
  existingDraftContent?: HMBlockNode[]
  /** Cursor position saved in the draft file; used to restore cursor on reload. */
  existingDraftCursorPosition?: number
  /** Block IDs the user previously touched in this draft, persisted across reloads (for rebase classifier). */
  /** Revision of the saved draft after reference maintenance. */
  existingDraftMaintenanceRevision?: number
  existingDraftRemovedChildDocumentIds?: string[]
  existingDraftMineTouchedIds?: string[]
  /** Three-way merge base captured at draft start or last rebase, persisted across reloads. */
  existingDraftBaseBlocks?: HMBlockNode[]
  /** Rename path persisted on the draft via the publish-popover rename affordance. */
  existingDraftPublishPath?: string[]
  /** The working schema persisted on the draft, when its document defines a type. */
  existingDraftSchemaDraft?: Record<string, any>
  existingDraftBindingSchemaDrafts?: Record<string, Record<string, any>>
  /** Base deps captured for the draft. Used by platform wrappers and tests. */
  existingDraftDeps?: string[]
  /** Platform-specific confirm workflow for discarding the synthetic versions-panel draft row. */
  draftVersionOnDiscardConfirm?: (draftId: string, send: (event: DocumentMachineEvent) => void) => void
  /** Pre-rendered document content HTML for SSR (avoids blank flash before editor loads) */
  ssrContentHTML?: string | null
  /** Platform-specific page footer (web only) */
  pageFooter?: ReactNode
  /** Starts loading a document route before navigation when the platform supports it. */
  onPrefetchDocument?: (id: UnpackedHypermediaId) => void

  floatingButtons?: ReactNode
  /** Inline child draft cards rendered after document content */
  inlineCards?: ReactNode
  /** Platform-specific actions rendered in the site header right side */
  rightActions?: ReactNode
  /** Callback to open edit profile dialog for site profile pages (only shown for own account) */
  onEditProfile?: () => void
  /** Additional header buttons for site profile pages (e.g., logout) - only shown for own account */
  profileHeaderButtons?: ReactNode
  /** Override follow button click on profile pages (web: saves intent + opens signup for unauthenticated users) */
  onFollowClick?: () => void
  /** Optional inline element injected after content blocks (subscribe box) */
  inlineInsert?: ReactNode
  /** Whether the current user can edit this document (drives the state machine guard). */
  canEdit?: boolean
  /** Optional provided machine (with actors) for desktop editing support. */
  machine?: typeof documentMachine
  /** Optional XState inspect callback for debugging. Gated by developerTools flag. */
  inspect?: (inspectionEvent: any) => void
  /** Inspect event store for the debug drawer to subscribe to. */
  inspectStore?: import('@shm/shared/models/document-machine-inspect').InspectEventStore
  /** Component to render document content using the editor. Optional during SSR. */
  DocumentContentComponent?: React.ComponentType<DocumentContentProps>
  /** Called when the editor instance is created. Used by desktop to capture editor ref for draft saving. */
  onEditorReady?: (editor: any) => void
  /** Extra components to render inside the DocumentMachineProvider (e.g. draft content loader). */
  machineExtras?: ReactNode
  /** Render prop for floating overlay when editing. Receives existing menu items so they can be merged. */
  editingFloatingActions?: (props: {menuItems: MenuItemType[]}) => ReactNode
  /** Render prop for floating overlay when a draft exists but not actively editing. Shown when a draft exists and isEditing is false. */
  draftActions?: (props: {menuItems: MenuItemType[]}) => ReactNode
  /** Signing account ID for draft saving (desktop only). Flows into machine context. */
  signingAccountId?: string
  /** Publish account UID for publishing (desktop only). Flows into machine context. */
  publishAccountUid?: string
  /** Async function that uploads a File to the daemon and resolves to its CID. Platform-specific. */
  fileUpload?: (file: File) => Promise<string>
  /** Account uid used in inline mention suggestions. */
  perspectiveAccountUid?: string | null
  /** Options passed to the link extension. */
  linkExtensionOptions?: LinkExtensionOptions
  /** Optional site-header edit-nav pane rendered inside DocumentMachineProvider. */
  editNavPane?: ReactNode
  /** Optional platform adapter for user-defined metadata autocomplete. */
  attributeAutocomplete?: AttributeAutocomplete
}

/** Get panel title for display */
function getPanelTitle(panelKey: string | null): string {
  switch (panelKey) {
    case 'activity':
    case 'feed':
      return 'Activity'
    case 'comments':
      return 'Comments'
    case 'directory':
      return 'Sub documents'
    case 'collaborators':
      return 'Collaborators'
    case 'all-documents':
      return 'All Documents'
    case 'options':
      return 'Document Options'
    default:
      return 'Panel'
  }
}

/** Renders a resource with a document-scoped queue for comment references. */
export function ResourcePage(props: ResourcePageProps) {
  return (
    <CommentDraftReferenceProvider key={props.docId.id}>
      <ResourcePageContent {...props} />
    </CommentDraftReferenceProvider>
  )
}

function ResourcePageContent({
  docId,
  resourceId,
  CommentEditor,
  optionsMenuItems,
  onExtendSchema,
  onNewTypedDocument,
  onNewTypedCollection,
  fileBrowserCreateMenuItem,
  fileBrowserOnIncludeDocument,
  fileBrowserGetIncludeDocumentState,
  extraMenuItems,
  existingDraft,
  reservedDraftId,
  existingDraftVisibility,
  existingDraftContent,
  existingDraftCursorPosition,
  existingDraftMaintenanceRevision,
  existingDraftRemovedChildDocumentIds,
  existingDraftMineTouchedIds,
  existingDraftBaseBlocks,
  existingDraftPublishPath,
  existingDraftSchemaDraft,
  existingDraftBindingSchemaDrafts,
  existingDraftDeps,
  draftVersionOnDiscardConfirm,
  floatingButtons,
  pageFooter,
  onPrefetchDocument,
  inlineCards,
  rightActions,
  onEditProfile,
  profileHeaderButtons,
  onFollowClick,
  inlineInsert,
  canEdit = false,
  machine,
  inspect,
  inspectStore,
  DocumentContentComponent,
  onEditorReady,
  machineExtras,
  editingFloatingActions,
  draftActions,
  signingAccountId,
  publishAccountUid,
  fileUpload,
  ssrContentHTML,
  perspectiveAccountUid,
  linkExtensionOptions,
  editNavPane,
  attributeAutocomplete,
}: ResourcePageProps) {
  const route = useNavRoute()
  const effectiveFileBrowserCreateMenuItem =
    fileBrowserCreateMenuItem === undefined
      ? optionsMenuItems?.find((item) => item.key === 'new')
      : fileBrowserCreateMenuItem
  const replaceRoute = useNavigate('replace')
  const isSiteProfile = route.key === 'site-profile'
  const media = useMedia()
  const [liveNavigationItems, setLiveNavigationItems] = useState<DocNavigationItem[] | undefined>()
  const publishError = usePublishError()
  const [editNavPanePortalElement, setEditNavPanePortalElement] = useState<HTMLDivElement | null>(null)

  const handleResourceRedirect = useCallback(
    ({isDeleted, redirectTarget}: {isDeleted: boolean; redirectTarget: UnpackedHypermediaId | null}) => {
      if (isDeleted || !redirectTarget) return
      const nextRoute = replaceRouteDocumentId(route, redirectTarget)
      const sourcePathName = docId.path?.join('/') || '/'
      const destinationPathName = redirectTarget.path?.join('/') || '/'
      const sourceDisplayPath = sourcePathName === '/' ? '/' : `/${sourcePathName}`
      const destinationDisplayPath = destinationPathName === '/' ? '/' : `/${destinationPathName}`
      replaceRoute(nextRoute)
      toast(`This document redirected from ${sourceDisplayPath} to ${destinationDisplayPath}.`)
    },
    [docId, replaceRoute, route],
  )

  const resourceFetchId = resourceId === undefined ? docId : resourceId
  const resource = useResource(resourceFetchId, {
    subscribed: true,
    recursive: true,
    onRedirectOrDeleted: handleResourceRedirect,
  })

  // Once a real document has been seen, keep DocumentMachineProvider mounted across any
  // subsequent transient resource failures (refetch errors, discovery flapping, transient
  // not-found from a stale daemon `latest` pointer). Without this sticky gate, the early
  // returns below would unmount DocumentBody and destroy the XState actor on each blip.
  // Reset on route changes so a newly-created local draft never reuses the parent
  // document while its draft record is resolving.
  const documentResourceRouteKey = getDocumentResourceRouteKey(docId)
  const lastGoodRouteIdRef = useRef(documentResourceRouteKey)
  const hasEverLoadedRef = useRef(false)
  const lastLoadedDocumentRef = useRef<HMDocument | null>(null)
  if (lastGoodRouteIdRef.current !== documentResourceRouteKey) {
    lastGoodRouteIdRef.current = documentResourceRouteKey
    hasEverLoadedRef.current = false
    lastLoadedDocumentRef.current = null
  }
  if (resourceFetchId && resource.data?.type === 'document' && resource.data.id.id === resourceFetchId.id) {
    hasEverLoadedRef.current = true
    lastLoadedDocumentRef.current = resource.data.document
  }

  // docId.uid determines the site header — for site-profile, docId IS the site context
  const siteHomeId = hmId(docId.uid, {latest: true})
  const siteHomeResource = useResource(siteHomeId, {subscribed: true})
  const latestDocumentResource = useResource(resourceFetchId ? latestId(resourceFetchId) : null, {subscribed: true})
  const latestDocumentVersion =
    latestDocumentResource.data?.type === 'document' ? latestDocumentResource.data.document.version : null
  const isLatest = useIsLatest(resourceFetchId, resource)

  const siteHomeDocument = siteHomeResource.data?.type === 'document' ? siteHomeResource.data.document : null

  // Compute header data
  const headerData = computeHeaderData(siteHomeDocument)

  // Comment handling: when resource is a comment, resolve and load its target document.
  // Hooks must be called unconditionally, so we always call useResource for the target
  // (it no-ops when targetDocId is null).
  const comment = resource.data?.type === 'comment' ? resource.data.comment : null
  const targetDocId = comment ? getCommentTargetId(comment) : null
  const targetResource = useResource(targetDocId, {
    subscribed: true,
    recursive: true,
  })

  // Transient (non-fatal) resource state surfaced as a banner under the site header
  // while the document keeps rendering. Only meaningful after the doc has loaded at
  // least once; before that, the regular loading/error branches still take over.
  const transientResourceError: TransientResourceError = (() => {
    if (!hasEverLoadedRef.current) return null
    if (resource.data?.type === 'error') {
      const msg = resource.data.message ?? 'Refresh failed'
      // Permission errors are persistent, not transient — leave to the regular branch.
      if (msg.toLowerCase().includes('permission')) return null
      return {kind: 'refetch-error', message: msg}
    }
    if (resource.isDiscovering) return {kind: 'discovering'}
    if (resource.data?.type === 'not-found') return {kind: 'not-found-transient'}
    return null
  })()

  // Site profile view: subscribe to and discover the profile account, then render profile content
  if (isSiteProfile) {
    const accountUid = route.key === 'site-profile' ? route.accountUid || docId.uid : docId.uid
    const tab = route.key === 'site-profile' ? route.tab : 'profile'
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={siteHomeId}
        headerData={headerData}
        document={siteHomeDocument || undefined}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <DocumentTopBar
          breadcrumbs={[{id: siteHomeId, metadata: siteHomeDocument?.metadata ?? {}}, {label: 'Profile'}]}
          isMobile={media.xs && !IS_DESKTOP}
        />
        <SiteProfileContent
          siteUid={docId.uid}
          accountUid={accountUid}
          tab={tab}
          onEditProfile={onEditProfile}
          headerButtons={profileHeaderButtons}
          onFollowClick={onFollowClick}
          pageFooter={pageFooter}
        />
      </PageWrapper>
    )
  }

  if (resourceFetchId === null && existingDraft === undefined && !reservedDraftId) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
        {pageFooter}
      </PageWrapper>
    )
  }

  // Drafts can target a path that has no published document yet (the user
  // is creating a new doc via the unified editor). When that's the case,
  // bypass discovery / not-found and render the editor over a placeholder
  // document so the document machine can transition to "loaded" → editing.
  const hasUnpublishedDraft = hasUnpublishedDraftForResourceState({
    existingDraft,
    reservedDraftId,
    resourceFetchId,
    resourceIsDiscovering: resource.isDiscovering,
    resourceData: resource.data,
  })

  // Loading state - should not show during SSR if data was prefetched
  if (resource.isInitialLoading && !hasUnpublishedDraft && !hasEverLoadedRef.current) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <div className="flex flex-1 items-center justify-center">
          <Spinner />
        </div>
        {pageFooter}
      </PageWrapper>
    )
  }

  // Handle discovery state
  if (resource.isDiscovering && !hasUnpublishedDraft && !hasEverLoadedRef.current) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <PageDiscovery />
        {pageFooter}
      </PageWrapper>
    )
  }

  // Handle not-found
  if ((!resource.data || resource.data.type === 'not-found') && !hasUnpublishedDraft && !hasEverLoadedRef.current) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <PageNotFound />
        {pageFooter}
      </PageWrapper>
    )
  }

  // Handle tombstone (deleted) — real deletion, not transient. Always unmount.
  if (!hasUnpublishedDraft && (resource.isTombstone || resource.data?.type === 'tombstone')) {
    const isCommentRoute = route.key === 'comments'
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <PageDeleted entityType={isCommentRoute ? 'comment' : 'document'} />
        {pageFooter}
      </PageWrapper>
    )
  }

  // Handle private document (permission denied) — persistent state, always unmount.
  if (resource.data?.type === 'error' && resource.data.message.toLowerCase().includes('permission')) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <PagePrivate />
        {pageFooter}
      </PageWrapper>
    )
  }

  // Handle error — only if we never loaded successfully. Otherwise fall through to the
  // success render path with a banner so the user keeps the document in view.
  if (!hasUnpublishedDraft && resource.data?.type === 'error' && !hasEverLoadedRef.current) {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <div className="flex flex-1 items-center justify-center p-8">
          <div className="text-destructive">{resource.data.message}</div>
        </div>
        {pageFooter}
      </PageWrapper>
    )
  }

  // Handle comment — show the target document's discussions view with this comment open.
  // Uses the same PageWrapper as all other branches so the site header stays mounted.
  if (comment) {
    if (!targetDocId || targetResource.data?.type === 'not-found') {
      return (
        <PageWrapper
          siteHomeId={siteHomeId}
          docId={docId}
          headerData={headerData}
          rightActions={rightActions}
          onPrefetchDocument={onPrefetchDocument}
          fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
        >
          <PageNotFound />
          {pageFooter}
        </PageWrapper>
      )
    }
    if (targetResource.isInitialLoading) {
      return (
        <PageWrapper
          siteHomeId={siteHomeId}
          docId={docId}
          headerData={headerData}
          rightActions={rightActions}
          onPrefetchDocument={onPrefetchDocument}
          fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
        >
          <div className="flex flex-1 items-center justify-center">
            <Spinner />
          </div>
          {pageFooter}
        </PageWrapper>
      )
    }
    if (targetResource.data?.type !== 'document') {
      return (
        <PageWrapper
          siteHomeId={siteHomeId}
          docId={docId}
          headerData={headerData}
          rightActions={rightActions}
          onPrefetchDocument={onPrefetchDocument}
          fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
        >
          <PageNotFound />
          {pageFooter}
        </PageWrapper>
      )
    }
    const targetDocument = targetResource.data.document
    return (
      <DocumentMachineProvider input={{documentId: targetDocId, canEdit: false}} inspect={inspect}>
        <PageWrapper
          siteHomeId={siteHomeId}
          docId={targetDocId}
          headerData={headerData}
          document={targetDocument}
          rightActions={rightActions}
          onPrefetchDocument={onPrefetchDocument}
          fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
        >
          <DocumentBody
            routeDocId={targetDocId}
            docId={targetDocId}
            document={targetDocument}
            activeView="comments"
            latestVersion={targetDocument.version}
            openComment={comment.id}
            existingDraft={false}
            CommentEditor={CommentEditor}
            siteUrl={siteHomeDocument?.metadata?.siteUrl}
            pageFooter={pageFooter}
            DocumentContentComponent={DocumentContentComponent}
            ssrContentHTML={ssrContentHTML}
            perspectiveAccountUid={perspectiveAccountUid}
            linkExtensionOptions={linkExtensionOptions}
          />
        </PageWrapper>
      </DocumentMachineProvider>
    )
  }

  // Success: render document. When the doc isn't published yet but a draft
  // exists, fabricate a placeholder so DocumentBody / the document machine
  // can transition to "loaded" → editing for the new-document case.
  let document: HMDocument
  const renderedDocId = getRenderedDocumentId(docId, resource.data, resourceFetchId)
  if (resourceFetchId && resource.data?.type === 'document') {
    document = resource.data.document
  } else if (lastLoadedDocumentRef.current) {
    // Transient refetch failure / not-found / discovery flap — keep showing the last
    // good document. The banner in PageWrapper informs the user.
    document = lastLoadedDocumentRef.current
  } else if (hasUnpublishedDraft) {
    document = {
      account: docId.uid,
      path: `/${(docId.path ?? []).join('/')}`,
      content: [],
      metadata: existingDraft && existingDraft.metadata ? existingDraft.metadata : {},
      visibility: existingDraftVisibility,
      version: '',
      authors: [],
      createTime: undefined as any,
      updateTime: undefined as any,
      genesis: '',
    } as unknown as HMDocument
  } else {
    return (
      <PageWrapper
        siteHomeId={siteHomeId}
        docId={docId}
        headerData={headerData}
        rightActions={rightActions}
        onPrefetchDocument={onPrefetchDocument}
        fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      >
        <PageNotFound />
        {pageFooter}
      </PageWrapper>
    )
  }

  const effectiveCanEdit = getEffectiveCanEdit({canEdit, resourceFetchId, existingDraft, reservedDraftId})
  const draftVersionEntry = existingDraft
    ? {
        docId: renderedDocId,
        draftId: existingDraft.id,
        deps: existingDraftDeps,
        metadata: existingDraft.metadata,
        onDiscardConfirm: draftVersionOnDiscardConfirm,
      }
    : undefined

  return (
    <PageWrapper
      siteHomeId={siteHomeId}
      docId={renderedDocId}
      headerData={headerData}
      document={document}
      rightActions={rightActions}
      onPrefetchDocument={onPrefetchDocument}
      fileBrowserCreateMenuItem={effectiveFileBrowserCreateMenuItem}
      fileBrowserOnIncludeDocument={fileBrowserOnIncludeDocument}
      fileBrowserGetIncludeDocumentState={fileBrowserGetIncludeDocumentState}
      editNavPanePortalRef={setEditNavPanePortalElement}
      transientResourceError={transientResourceError}
      liveNavigationItems={liveNavigationItems}
    >
      {publishError.content}
      <DocumentMachineProvider
        // Recreate the machine only when the identity it's bound to changes: the
        // resolved doc id (path change on first publish) or the ROUTE's pinned
        // version (?v=). Deliberately NOT the *resolved* latest version — that
        // bumps on our own publish, which would destroy + recreate the machine
        // mid-publish and re-load the just-cleared draft, stranding it in editing.
        key={getDocumentMachineKey(renderedDocId)}
        input={{
          documentId: renderedDocId,
          canEdit: effectiveCanEdit,
          isLatest,
          routeVersion: renderedDocId.version ?? null,
          deps: existingDraftDeps,
          reservedDraftId: reservedDraftId ?? undefined,
          editUid: renderedDocId.uid,
          editPath: renderedDocId.path ?? undefined,
          signingAccountId,
          publishAccountUid,
        }}
        machine={machine}
        inspect={inspect}
        onPublishError={publishError.showError}
      >
        <DocumentNavigationItemsBridge docId={renderedDocId} onItemsChange={setLiveNavigationItems} />
        {editNavPane && editNavPanePortalElement ? createPortal(editNavPane, editNavPanePortalElement) : null}
        <AttributeAutocompleteProvider value={attributeAutocomplete}>
          <DocumentBody
            routeDocId={docId}
            docId={renderedDocId}
            document={document}
            documentSyncRouteKey={documentResourceRouteKey}
            documentIsPlaceholderData={getDocumentSyncIsPlaceholderData({
              resourceFetchId,
              hasUnpublishedDraft,
              resourceIsPreviousData: resource.isPreviousData,
            })}
            activeView={getActiveView(route.key)}
            isLatest={isLatest}
            latestVersion={latestDocumentVersion}
            siteUrl={siteHomeDocument?.metadata?.siteUrl}
            CommentEditor={CommentEditor}
            optionsMenuItems={optionsMenuItems}
            onExtendSchema={onExtendSchema}
            onNewTypedDocument={onNewTypedDocument}
            onNewTypedCollection={onNewTypedCollection}
            extraMenuItems={extraMenuItems}
            existingDraft={existingDraft}
            reservedDraftId={reservedDraftId}
            existingDraftVisibility={existingDraftVisibility}
            existingDraftContent={existingDraftContent}
            existingDraftCursorPosition={existingDraftCursorPosition}
            existingDraftMaintenanceRevision={existingDraftMaintenanceRevision}
            existingDraftRemovedChildDocumentIds={existingDraftRemovedChildDocumentIds}
            existingDraftMineTouchedIds={existingDraftMineTouchedIds}
            existingDraftBaseBlocks={existingDraftBaseBlocks}
            existingDraftPublishPath={existingDraftPublishPath}
            existingDraftSchemaDraft={existingDraftSchemaDraft}
            existingDraftBindingSchemaDrafts={existingDraftBindingSchemaDrafts}
            existingDraftDeps={existingDraftDeps}
            draftVersionEntry={draftVersionEntry}
            floatingButtons={floatingButtons}
            pageFooter={pageFooter}
            inlineCards={inlineCards}
            inlineInsert={inlineInsert}
            DocumentContentComponent={DocumentContentComponent}
            onEditorReady={onEditorReady}
            canEdit={effectiveCanEdit}
            editingFloatingActions={editingFloatingActions}
            draftActions={draftActions}
            signingAccountId={signingAccountId}
            publishAccountUid={publishAccountUid}
            fileUpload={fileUpload}
            ssrContentHTML={ssrContentHTML}
            perspectiveAccountUid={perspectiveAccountUid}
            linkExtensionOptions={linkExtensionOptions}
            transientResourceError={transientResourceError}
          />
        </AttributeAutocompleteProvider>
        <ChildDeletionPublishDialog />
        {machineExtras}
        {inspect && (
          <Suspense fallback={null}>
            <LazyDocumentMachineDebugDrawer store={inspectStore} />
          </Suspense>
        )}
      </DocumentMachineProvider>
    </PageWrapper>
  )
}

// Header data computed from site home document
export interface HeaderData {
  items: DocNavigationItem[]
  homeNavigationItems: DocNavigationItem[]
  directoryItems: DocNavigationItem[]
  isCenterLayout: boolean
  siteHomeDocument: HMDocument | null
}

export function computeHeaderData(siteHomeDocument: HMDocument | null): HeaderData {
  // Top navigation is manual-only for now. If the home document has no
  // explicit `navigation` detached block, the header stays empty rather than
  // inferring items from the document hierarchy.
  const navigationBlockNode = siteHomeDocument?.detachedBlocks?.navigation
  const homeNavigationItems: DocNavigationItem[] = navigationBlockNode
    ? navigationBlockNode.children
        ?.map((child) => {
          const linkBlock = child.block.type === 'Link' ? child.block : null
          if (!linkBlock) return null
          const id = unpackHmId(linkBlock.link)
          return {
            isPublished: true,
            isDraft: false,
            key: linkBlock.id,
            metadata: {name: linkBlock.text || ''},
            id: id || undefined,
            webUrl: id ? undefined : linkBlock.link,
          } as DocNavigationItem
        })
        .filter((item): item is DocNavigationItem => item !== null && isValidSiteHeaderItem(item)) ?? []
    : []

  const isCenterLayout =
    siteHomeDocument?.metadata?.theme?.headerLayout === 'Center' ||
    siteHomeDocument?.metadata?.layout === 'Seed/Experimental/Newspaper'

  return {
    items: homeNavigationItems,
    homeNavigationItems,
    directoryItems: [],
    isCenterLayout,
    siteHomeDocument,
  }
}

function getLiveHeaderNavigationItems(
  machineNav: ReturnType<typeof useDocumentNavigationOptional>,
): DocNavigationItem[] | undefined {
  return machineNav
    ?.map((n) => {
      const id = unpackHmId(n.link)
      return {
        key: n.id,
        id: id ?? undefined,
        webUrl: id ? undefined : n.link,
        draftId: undefined,
        metadata: {name: n.text || ''},
        isPublished: true,
      } satisfies DocNavigationItem
    })
    .filter(isValidSiteHeaderItem)
}

function DocumentNavigationItemsBridge({
  docId,
  onItemsChange,
}: {
  docId: UnpackedHypermediaId
  onItemsChange: (items: DocNavigationItem[] | undefined) => void
}) {
  const machineNav = useDocumentNavigationOptional()
  const homeDraftOverride = useIsHomeDraftOverride()
  const isHomeDoc = homeDraftOverride ?? !docId.path?.length

  useEffect(() => {
    onItemsChange(isHomeDoc ? getLiveHeaderNavigationItems(machineNav) : undefined)
    return () => onItemsChange(undefined)
  }, [docId.id, isHomeDoc, machineNav, onItemsChange])

  return null
}

/** Props for the persistent site page shell. */
export interface PageShellProps {
  siteHomeId: UnpackedHypermediaId
  docId: UnpackedHypermediaId
  headerData: HeaderData
  document?: HMDocument
  children: React.ReactNode
  rightActions?: React.ReactNode
  editNavPane?: React.ReactNode
  editNavPanePortalRef?: (node: HTMLDivElement | null) => void
  /** Non-fatal resource fetch state rendered as a banner below the header. */
  transientResourceError?: TransientResourceError
  /** In-flight header navigation from an active document machine. */
  liveNavigationItems?: DocNavigationItem[]
  /** Starts loading a document route before navigation when the platform supports it. */
  onPrefetchDocument?: (id: UnpackedHypermediaId) => void
  fileBrowserCreateMenuItem?: MenuItemType | null
  fileBrowserOnIncludeDocument?: import('./site-file-browser').SiteFileBrowserProps['onIncludeDocument']
  fileBrowserGetIncludeDocumentState?: import('./site-file-browser').SiteFileBrowserProps['getIncludeDocumentState']
}

/** Persistent site chrome for the header and file browser around route content. */
export function PageShell({
  siteHomeId,
  docId,
  headerData,
  document,
  children,
  rightActions,
  editNavPane,
  editNavPanePortalRef,
  transientResourceError,
  liveNavigationItems,
  onPrefetchDocument,
  fileBrowserCreateMenuItem,
  fileBrowserOnIncludeDocument,
  fileBrowserGetIncludeDocumentState,
}: PageShellProps) {
  // Mobile: let content flow naturally (document scroll)
  // Desktop: fixed height container (element scroll via ScrollArea in children)
  // Note: IS_DESKTOP (Electron) never uses document scroll regardless of window width
  const media = useMedia()
  const isMobile = media.xs && !IS_DESKTOP
  const navigate = useNavigate()
  const [isFileBrowserOpen, setIsFileBrowserOpen] = useState(false)
  const itemsForHeader = liveNavigationItems ?? headerData.items

  return (
    <WebActivityPanelLayout>
      <SiteFileBrowserLayout
        siteId={hmId(siteHomeId.uid)}
        activeDocumentId={docId}
        siteName={getMetadataName(headerData.siteHomeDocument?.metadata) || 'Space documents'}
        mobileOpen={isFileBrowserOpen}
        onMobileOpenChange={setIsFileBrowserOpen}
        onPrefetch={onPrefetchDocument}
        createMenuItem={fileBrowserCreateMenuItem}
        onIncludeDocument={fileBrowserOnIncludeDocument}
        getIncludeDocumentState={fileBrowserGetIncludeDocumentState}
        onNavigate={(id) => {
          setIsFileBrowserOpen(false)
          navigate({key: 'document', id})
        }}
      >
        <div
          // The SSR fallback for the measured --site-header-live-h. The values
          // live in a stylesheet (web app styles.css) rather than an inline
          // style because the header height is responsive; only the pre-hydration
          // web paint ever reads them.
          data-header-layout={headerData.isCenterLayout ? 'center' : 'bar'}
          className={cn(
            'dark:bg-background flex max-h-full flex-col bg-white',
            // On desktop: fill viewport height for element scrolling (use dvh for mobile browsers)
            // On mobile: natural height for document scrolling
            isMobile ? 'min-h-dvh has-[[data-document-page-main]]:h-dvh' : 'h-dvh',
          )}
        >
          <SiteHeader
            siteHomeId={siteHomeId}
            docId={docId}
            items={itemsForHeader}
            homeNavigationItems={headerData.homeNavigationItems}
            directoryItems={headerData.directoryItems}
            isCenterLayout={headerData.isCenterLayout}
            document={document}
            siteHomeDocument={headerData.siteHomeDocument}
            notifyServiceHost={NOTIFY_SERVICE_HOST}
            rightActions={rightActions}
            editNavPane={editNavPanePortalRef ? undefined : editNavPane}
            editNavPanePortalRef={editNavPanePortalRef}
          />
          <TransientResourceBanner error={transientResourceError ?? null} />
          <div className="flex min-h-0 flex-1 flex-col">{children}</div>
        </div>
      </SiteFileBrowserLayout>
    </WebActivityPanelLayout>
  )
}

/** Wrapper that renders the persistent site header, file browser, and page content. */
export function PageWrapper({
  siteHomeId,
  docId,
  headerData,
  document,
  children,
  rightActions,
  editNavPane,
  editNavPanePortalRef,
  transientResourceError,
  liveNavigationItems,
  onPrefetchDocument,
  fileBrowserCreateMenuItem,
  fileBrowserOnIncludeDocument,
  fileBrowserGetIncludeDocumentState,
}: {
  siteHomeId: UnpackedHypermediaId
  docId: UnpackedHypermediaId
  headerData: HeaderData
  document?: HMDocument
  children: React.ReactNode
  rightActions?: React.ReactNode
  editNavPane?: React.ReactNode
  editNavPanePortalRef?: (node: HTMLDivElement | null) => void
  /** Non-fatal resource fetch state rendered as a banner below the header. */
  transientResourceError?: TransientResourceError
  /** In-flight header navigation from a document machine mounted inside this wrapper. */
  liveNavigationItems?: DocNavigationItem[]
  /** Starts loading a document route before navigation when the platform supports it. */
  onPrefetchDocument?: (id: UnpackedHypermediaId) => void
  fileBrowserCreateMenuItem?: MenuItemType | null
  fileBrowserOnIncludeDocument?: import('./site-file-browser').SiteFileBrowserProps['onIncludeDocument']
  fileBrowserGetIncludeDocumentState?: import('./site-file-browser').SiteFileBrowserProps['getIncludeDocumentState']
}) {
  // Live-preview the in-flight nav while the user edits the home doc, so
  // additions/reorders/deletions in the EditNavPopover show immediately in
  // the visible site header. Mirrors the legacy draft route at
  // frontend/apps/desktop/src/pages/draft.tsx:880-893. Returns undefined
  // outside DocumentMachineProvider (loading/error/discovery branches), in
  // which case we fall back to the published headerData.items.
  const machineNav = useDocumentNavigationOptional()
  const homeDraftOverride = useIsHomeDraftOverride()
  const isHomeDoc = homeDraftOverride ?? !docId.path?.length
  const liveItems = liveNavigationItems ?? (isHomeDoc ? getLiveHeaderNavigationItems(machineNav) : undefined)

  return (
    <PageShell
      siteHomeId={siteHomeId}
      docId={docId}
      headerData={headerData}
      document={document}
      rightActions={rightActions}
      editNavPane={editNavPane}
      editNavPanePortalRef={editNavPanePortalRef}
      transientResourceError={transientResourceError}
      liveNavigationItems={liveItems}
      onPrefetchDocument={onPrefetchDocument}
      fileBrowserCreateMenuItem={fileBrowserCreateMenuItem}
      fileBrowserOnIncludeDocument={fileBrowserOnIncludeDocument}
      fileBrowserGetIncludeDocumentState={fileBrowserGetIncludeDocumentState}
    >
      {children}
    </PageShell>
  )
}

function TransientResourceBanner({error}: {error: TransientResourceError}) {
  if (!error) return null
  const tone =
    error.kind === 'refetch-error'
      ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
      : 'bg-muted text-muted-foreground'
  let message: string
  switch (error.kind) {
    case 'refetch-error':
      message = `Couldn't refresh from peers. Showing last loaded version. (${error.message})`
      break
    case 'discovering':
      message = 'Looking for newer version…'
      break
    case 'not-found-transient':
      message = 'Document temporarily unreachable. Retrying…'
      break
  }
  return (
    <div role="status" className={cn('px-4 py-2 text-xs', tone)} data-testid="transient-resource-banner">
      {message}
    </div>
  )
}

/** Distance the document must scroll before its back-to-top control appears. */
export const BACK_TO_TOP_SCROLL_OFFSET = 200

// Document body with content
function DocumentBody({
  routeDocId,
  docId,
  document,
  onExtendSchema,
  onNewTypedDocument,
  onNewTypedCollection,
  documentSyncRouteKey,
  documentIsPlaceholderData,
  activeView,
  isLatest = true,
  latestVersion,
  siteUrl,
  CommentEditor,
  optionsMenuItems,
  extraMenuItems,
  existingDraft,
  reservedDraftId,
  existingDraftVisibility,
  existingDraftContent,
  existingDraftCursorPosition,
  existingDraftMaintenanceRevision,
  existingDraftRemovedChildDocumentIds,
  existingDraftMineTouchedIds,
  existingDraftBaseBlocks,
  existingDraftPublishPath,
  existingDraftSchemaDraft,
  existingDraftBindingSchemaDrafts,
  existingDraftDeps,
  draftVersionEntry,
  floatingButtons,
  pageFooter,
  inlineCards,
  openComment,
  inlineInsert,
  DocumentContentComponent,
  onEditorReady,
  canEdit = false,
  editingFloatingActions,
  draftActions,
  signingAccountId,
  publishAccountUid,
  fileUpload,
  ssrContentHTML,
  perspectiveAccountUid,
  linkExtensionOptions,
  transientResourceError,
}: {
  routeDocId: UnpackedHypermediaId
  docId: UnpackedHypermediaId
  document: HMDocument
  /** Identifies intentional route-level version changes for document synchronization. */
  documentSyncRouteKey?: string
  /** Whether React Query is retaining the previous route's document while the selected version loads. */
  documentIsPlaceholderData?: boolean
  /** Which tab/view to display */
  activeView: ActiveView
  isLatest?: boolean
  latestVersion?: string | null
  siteUrl?: string
  CommentEditor?: React.ComponentType<CommentEditorProps>
  optionsMenuItems?: MenuItemType[]
  /** Platform handler for "Extend Schema" (see {@link ResourcePageProps.onExtendSchema}). */
  onExtendSchema?: (baseSchemaCid: string) => void
  /** See {@link ResourcePageProps.onNewTypedDocument}. */
  onNewTypedDocument?: (schemaUrl: string) => void
  /** See {@link ResourcePageProps.onNewTypedCollection}. */
  onNewTypedCollection?: (schemaUrl: string) => void
  extraMenuItems?: MenuItemType[]
  existingDraft?: HMExistingDraft | false
  reservedDraftId?: string | null
  existingDraftVisibility?: HMDocument['visibility']
  existingDraftContent?: HMBlockNode[]
  existingDraftCursorPosition?: number
  /** Revision of the saved draft after reference maintenance. */
  existingDraftMaintenanceRevision?: number
  existingDraftRemovedChildDocumentIds?: string[]
  existingDraftMineTouchedIds?: string[]
  existingDraftBaseBlocks?: HMBlockNode[]
  existingDraftPublishPath?: string[]
  existingDraftSchemaDraft?: Record<string, any>
  existingDraftBindingSchemaDrafts?: Record<string, Record<string, any>>
  existingDraftDeps?: string[]
  draftVersionEntry?: DraftVersionEntry
  floatingButtons?: ReactNode
  pageFooter?: ReactNode
  inlineCards?: ReactNode
  /** Comment to open in discussions view (used when navigating to a comment entity) */
  openComment?: string
  /** Optional inline element injected after content blocks */
  inlineInsert?: ReactNode
  /** Component to render document content using the editor */
  DocumentContentComponent?: React.ComponentType<DocumentContentProps>
  /** Called when the editor instance is created */
  onEditorReady?: (editor: any) => void
  /** Whether the current user can edit this document */
  canEdit?: boolean
  /** Render prop for floating overlay when editing */
  editingFloatingActions?: (props: {menuItems: MenuItemType[]}) => ReactNode
  /** Render prop for floating overlay when a draft exists but not actively editing */
  draftActions?: (props: {menuItems: MenuItemType[]}) => ReactNode
  /** Signing account ID for draft saving (desktop only) */
  signingAccountId?: string
  /** Publish account UID for publishing (desktop only) */
  publishAccountUid?: string
  /** Async function that uploads a File to the daemon and resolves to its CID */
  fileUpload?: (file: File) => Promise<string>
  ssrContentHTML?: string | null
  /** Account uid used in inline mention suggestions. */
  perspectiveAccountUid?: string | null
  /** Options passed to the link extension. */
  linkExtensionOptions?: LinkExtensionOptions
  /** Non-fatal resource fetch state synced into the machine context. */
  transientResourceError?: TransientResourceError
}) {
  // Sync document into state machine
  useDocumentSync(document, documentSyncRouteKey, documentIsPlaceholderData)
  // Sync canEdit changes into the machine (for account switching)
  useCapabilitySync(canEdit)
  // Sync isLatest changes into the machine (for old-version edit guard)
  useVersionLatestSync(isLatest, docId.version ?? null)
  // Sync account IDs into the machine (for draft saving / publishing)
  useAccountSync(signingAccountId, publishAccountUid)
  // Forward scroll events from the scroll container to the machine
  useScrollSync()
  // Surface transient resource fetch state inside the machine for debug / inner consumers.
  useResourceTransientSync(transientResourceError ?? null)
  // Sync draft resolution — machine stays in loading until this settles.
  // undefined = still loading, {draftId: null} = no draft, {draftId: string} = draft + content ready
  const draftResolution = useMemo(() => {
    let result:
      | {
          draftId: string | null
          content: HMBlockNode[] | null
          cursorPosition: number | null
          metadata?: import('@seed-hypermedia/client/hm-types').HMMetadata | null
          deps?: string[] | null
          maintenanceRevision?: number
          removedChildDocumentIds?: string[] | null
          mineTouchedIds?: string[] | null
          baseBlocks?: HMBlockNode[] | null
          publishPath?: string[] | null
          schemaDraft?: Record<string, any> | null
          bindingSchemaDrafts?: Record<string, Record<string, any>> | null
        }
      | undefined
    if (existingDraft === undefined) {
      result = undefined
    } else if (!existingDraft) {
      result = {draftId: null as string | null, content: null, cursorPosition: null}
    } else if (existingDraftContent) {
      result = {
        draftId: existingDraft.id,
        content: existingDraftContent,
        cursorPosition: existingDraftCursorPosition ?? null,
        metadata: existingDraft.metadata ?? null,
        deps: existingDraftDeps ?? null,
        maintenanceRevision: existingDraftMaintenanceRevision ?? 0,
        removedChildDocumentIds: existingDraftRemovedChildDocumentIds ?? null,
        mineTouchedIds: existingDraftMineTouchedIds ?? null,
        baseBlocks: existingDraftBaseBlocks ?? null,
        publishPath: existingDraftPublishPath ?? null,
        schemaDraft: existingDraftSchemaDraft ?? null,
        bindingSchemaDrafts: existingDraftBindingSchemaDrafts ?? null,
      }
    } else {
      result = undefined // draft found but content not loaded yet
    }
    return result
  }, [
    existingDraft,
    existingDraftContent,
    existingDraftCursorPosition,
    existingDraftDeps,
    existingDraftMaintenanceRevision,
    existingDraftRemovedChildDocumentIds,
    existingDraftMineTouchedIds,
    existingDraftBaseBlocks,
    existingDraftPublishPath,
    existingDraftSchemaDraft,
    existingDraftBindingSchemaDrafts,
  ])
  useDraftResolutionSync(draftResolution)
  const publishedVersion = useDocumentSelector(selectPublishedVersion)
  const isEditing = useDocumentSelector(selectIsEditing)
  const isUnpublishedDraft = useDocumentSelector(selectIsUnpublishedDraft)
  const ctx = useDocumentSelector(selectContext)
  const documentPage = activeView === 'content' && !isEditing && !isUnpublishedDraft ? documentPageCid(document) : null
  const [editPageParameters, setEditPageParameters] = useState<(() => void) | null>(null)
  const onPageParametersChange = useCallback((edit: (() => void) | null) => setEditPageParameters(() => edit), [])
  const [managePagePermissions, setManagePagePermissions] = useState<(() => void) | null>(null)
  const onPageAccessChange = useCallback((revoke: (() => void) | null) => setManagePagePermissions(() => revoke), [])
  const draftOverlayAllowed = useDocumentSelector(selectDraftOverlayAllowed)
  const canEditCurrentRoute = useDocumentSelector(selectCanEditCurrentRoute)
  const shouldUseDraftOverlay = useDocumentSelector(selectShouldUseDraftOverlay)
  const isCollection = useDocumentSelector(selectIsCollection)
  const [mainCommentsFooter, setMainCommentsFooter] = useState<HTMLDivElement | null>(null)
  const [pendingDocumentConversion, setPendingDocumentConversion] = useState<'collection' | 'document' | null>(null)
  const collectionQueryBlock = useDocumentSelector(selectCollectionQueryBlock)
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  // Draft metadata (partial) overrides published metadata, same as the options panel.
  const metadata = useMemo(
    () =>
      draftOverlayAllowed
        ? {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
        : ctx.document?.metadata || document.metadata || {},
    [draftOverlayAllowed, ctx.document?.metadata, document.metadata, ctx.metadata],
  )
  const effectiveContent = useDocumentSelector(selectRenderableBlocks)
  // Set of block ids present in the currently published version of the document.
  const publishedBlockIds = useMemo(() => {
    const ids = new Set<string>()
    const walk = (nodes: HMBlockNode[] | undefined) => {
      if (!nodes) return
      for (const node of nodes) {
        if (node.block?.id) ids.add(node.block.id)
        if (node.children) walk(node.children)
      }
    }
    walk(document?.content ?? [])
    return ids
  }, [document?.content])
  const isBlockInPublishedVersion = useCallback(
    (blockId: string) => publishedBlockIds.has(blockId),
    [publishedBlockIds],
  )
  const removeCover = useCallback(() => {
    beginEditIfNeeded()
    send({type: 'change', metadata: {cover: ''}})
  }, [beginEditIfNeeded, send])
  const changeCover = useCallback(
    async (file: File) => {
      if (!fileUpload) return
      const cid = await fileUpload(await processImage(file, COVER_IMAGE_POLICY))
      beginEditIfNeeded()
      send({type: 'change', metadata: {cover: cid.startsWith('ipfs://') ? cid : `ipfs://${cid}`}})
    },
    [beginEditIfNeeded, fileUpload, send],
  )

  // Capture the editor instance locally and forward to upstream onEditorReady.
  // The local state drives useAutoRebase (auto-rebase on remote updates during editing).
  const [autoRebaseEditor, setAutoRebaseEditor] = useState<any>(null)
  const handleEditorReadyWrapped = useCallback(
    (editor: any) => {
      setAutoRebaseEditor(editor)
      onEditorReady?.(editor)
    },
    [onEditorReady],
  )
  useAutoRebase({
    editor: autoRebaseEditor,
    suppressChangeRef: autoRebaseEditor?._suppressChangeRef,
    onAutoMerged: (author) => {
      const msg = author ? `Draft updated with ${author}'s latest changes.` : `Draft updated to latest version.`
      toast.success(msg)
    },
    // Conflict path applies a mine-wins merge automatically (publish is never
    // blocked). The toast informs the user so they can review the other side's
    // changes if desired. Phase B will surface a per-block picker.
    onConflictDetected: ({conflictedBlockIds, author}) => {
      const blockCount = conflictedBlockIds.length
      const blockNoun = blockCount === 1 ? 'block' : 'blocks'
      const who = author ? `${author}` : 'another author'
      toast.info(`${who} also edited ${blockCount} ${blockNoun} — your version was kept.`, {duration: 6000})
    },
  })

  const route = useNavRoute()
  const navigate = useNavigate()
  const replaceRoute = useNavigate('replace')
  const showOlderVersionToast = shouldShowOlderVersionToast({routeDocId, latestVersion})
  const olderVersionToastId = getOlderVersionToastId(routeDocId)

  useEffect(() => {
    return () => {
      toast.dismiss(olderVersionToastId)
    }
  }, [olderVersionToastId])

  useEffect(() => {
    if (!showOlderVersionToast) {
      toast.dismiss(olderVersionToastId)
      return
    }

    toast('Older version linked', {
      id: olderVersionToastId,
      description: 'The author has published updates since this document version was created',
      duration: 10_000,
      position: 'bottom-right',
      action: {
        label: 'Go to latest',
        onClick: () => {
          replaceRoute(getLatestRouteForCurrentDocumentRoute(route))
          toast.dismiss(olderVersionToastId)
        },
      },
    })
  }, [olderVersionToastId, replaceRoute, route, showOlderVersionToast])
  useOldVersionEditBlocked(() => {
    toast('This version is read-only', getOldVersionEditBlockedToastOptions(routeDocId))
  })

  // Extract panel from route (only document/feed routes have panels)
  const panelRoute = getRoutePanel(route) as DocumentPanelRoute | null
  const panelKey = panelRoute?.key ?? null
  const commentsPanelTarget = getCommentsPanelTarget(docId, panelRoute)
  const expandedPanel = getExpandedDocumentPanel(route)
  const fullWidthHeader = isCollection && !expandedPanel
  const [inlineCollapseButton, setInlineCollapseButton] = useState<HTMLButtonElement | null>(null)
  const collapseExpandedPanel = () => {
    if (expandedPanel) navigate({key: 'document', id: docId, panel: expandedPanel.panel})
  }

  // Extract discussions-specific params from route or from explicit props
  const discussionsParams =
    route.key === 'comments'
      ? {
          openComment: route.openComment,
          replyCommentId: route.replyCommentId,
          targetBlockId: route.targetBlockId,
          blockId: route.blockId,
          blockRange: route.blockRange,
          autoFocus: route.autoFocus,
          isReplying: route.isReplying,
          replyCommentVersion: route.replyCommentVersion,
          rootReplyCommentVersion: route.rootReplyCommentVersion,
        }
      : openComment
        ? {openComment}
        : undefined
  const suppressMainCommentEditor = shouldSuppressMainCommentEditor({
    docId,
    activeView,
    discussionsParams,
    panelRoute,
  })

  // Respect the showActivity metadata toggle to hide the document tools bar.
  const showActivity = metadata?.showActivity !== false

  // Explore searches the whole space rather than presenting a document,
  // so it drops the document's chrome.
  const showDocumentChrome = activeView !== 'explore'
  // A mounted agent's document is the agent: no cover, icon, title, byline or timestamp above
  // its tabs, which are the whole page.
  const isAgentDocument = document.metadata.type === 'hm://hyper.media/agent'
  // An agent view (sessions, settings, ...) scrolls inside its own pane with a pinned composer, so
  // the page around it stops scrolling and hands it the remaining height, as a document page does.
  // An agent document's base URL opens its sessions, so it lays out like any agent view.
  const isAgentView = route.key === 'document' && (!!route.agentView || isAgentDocument)
  const fixedHeightMain = !!documentPage || isAgentView

  // Extract blockRef from route for scroll-to-block and highlighting
  const routeBlockRef = route.key === 'document' ? route.id.blockRef : null
  const sourceBlocks = shouldUseDraftOverlay ? effectiveContent : document.content
  const blockExists = useMemo(
    () =>
      routeBlockRef && !documentIsPlaceholderData && (!shouldUseDraftOverlay || draftResolution !== undefined)
        ? !!findContentBlock(sourceBlocks ?? [], routeBlockRef)
        : undefined,
    [routeBlockRef, documentIsPlaceholderData, shouldUseDraftOverlay, draftResolution, sourceBlocks],
  )
  const {scrollToBlock} = useBlockScroll(routeBlockRef, {navigationKey: route, blockExists})

  // On mount, sync URL hash (#blockId) into route if not already present
  useEffect(() => {
    if (typeof window === 'undefined') return
    const hash = window.location.hash
    if (!hash) return
    const fragment = parseFragment(hash.substring(1))
    if (!fragment?.blockId) return
    const blockRange =
      'start' in fragment && 'end' in fragment
        ? {start: fragment.start, end: fragment.end}
        : 'expanded' in fragment && fragment.expanded
          ? {expanded: true}
          : null
    // For comments routes, sync fragment into blockId/blockRange (comment block selection)
    if (route.key === 'comments') {
      if (route.blockId) return // already have block selection
      replaceRoute({
        ...route,
        id: {...route.id, blockRef: fragment.blockId, blockRange},
        blockId: fragment.blockId,
        blockRange,
      })
      return
    }
    if (route.key !== 'document' && route.key !== 'feed') return
    if (routeBlockRef) return // already have blockRef from route
    replaceRoute({
      ...route,
      id: {
        ...route.id,
        blockRef: fragment.blockId,
        blockRange,
      },
    })
  }, []) // only on mount

  const homeDraftOverride = useIsHomeDraftOverride()
  const isHomeDoc = homeDraftOverride ?? !docId.path?.length
  const draftVisibility = shouldUseDraftOverlay && existingDraft ? existingDraftVisibility : undefined
  const headerVisibility =
    document.visibility === 'PRIVATE' || draftVisibility === 'PRIVATE' ? 'PRIVATE' : document.visibility
  const versionHeadCount = getVersionHeads(document.version).length
  const siteId = useMemo(() => hmId(docId.uid), [docId.uid])
  // Skip the queries for anonymous pending drafts.
  const isLocalOnlyDoc = isPendingSpaceUid(docId.uid)
  const siteMembers = useSiteMembers(isLocalOnlyDoc ? null : siteId)
  const directory = useDirectory(isLocalOnlyDoc ? null : docId)
  const interactionSummary = useInteractionSummary(isLocalOnlyDoc ? null : docId)
  const commentsPanelSummary = useInteractionSummary(
    !isLocalOnlyDoc && panelKey === 'comments' ? commentsPanelTarget.docId : null,
  )
  const collaborators = useDocumentCollaborators(isLocalOnlyDoc ? null : docId)
  const peopleCount = useMemo(
    () => getRenderedCollaboratorsCount(collaborators.data, isHomeDoc),
    [collaborators.data, isHomeDoc],
  )
  const showMembersFacepile =
    isHomeDoc &&
    showDocumentChrome &&
    activeView !== 'all-documents' &&
    !siteMembers.isInitialLoading &&
    siteMembers.members.length > 0
  const citationsTargetId = useMemo(
    () => getCitationsTargetId({docId, documentVersion: document.version}),
    [docId, document.version],
  )
  const citations = useCitations(citationsTargetId)
  const connectionCount = useMemo(
    () => groupDocumentConnections(docId, citations.data?.citations).length,
    [docId, citations.data],
  )
  const isConnectionsPanel =
    panelRoute?.key === 'activity' &&
    !isDocumentVersionsPanelRoute(panelRoute) &&
    (!!panelRoute.targetBlockId || activityFilterToSlug(panelRoute.filterEventType) === 'citations')
  const panelCitations = useCitations(isConnectionsPanel ? panelRoute.id ?? citationsTargetId : null)

  // Breadcrumbs: fetch parent documents for non-home docs
  const breadcrumbIds = useMemo(() => {
    if (isHomeDoc) return []
    return getBreadcrumbDocumentIds(docId)
  }, [docId, isHomeDoc])

  // Local drafts override published metadata for breadcrumb segments. The
  // provider returns an empty list on platforms without local drafts (e.g.
  // current web), so the daemon-fetch path stays unchanged there.
  const accountDrafts = useDraftsForAccountSafe(docId.uid)

  // Resolve draft overlays without losing the published metadata they inherit.
  const draftsForBreadcrumbs = useMemo(
    () => breadcrumbIds.map((id) => findDraftForPath(accountDrafts.data, id.uid, id.path ?? [])),
    [breadcrumbIds, accountDrafts.data],
  )

  const reservedDraftBreadcrumbNames = useMemo(
    () => breadcrumbIds.map((id) => getReservedLazyDraftBreadcrumbName(id.path?.at(-1), reservedDraftId)),
    [breadcrumbIds, reservedDraftId],
  )

  // Positions where the draft list is still loading but the path *looks*
  // like a draft (placeholder `-` segment). We treat them as loading rather
  // than firing a daemon fetch we'll discard once the local list resolves.
  // Preallocated lazy draft ids are already known locally, so render their
  // stable placeholder breadcrumb immediately instead of a loading spinner.
  const pendingDraftLookup = useMemo(
    () =>
      breadcrumbIds.map(
        (id, i) => accountDrafts.isLoading && isDraftPathSegment(id.path?.at(-1)) && !reservedDraftBreadcrumbNames[i],
      ),
    [breadcrumbIds, accountDrafts.isLoading, reservedDraftBreadcrumbNames],
  )

  // Only unpublished placeholders lack a server resource. Existing documents
  // still supply the title when their draft contains only body edits.
  const resourceFetchIds = useMemo(
    () =>
      breadcrumbIds.map((id, i) =>
        isDraftPlaceholderPath(id.path, draftsForBreadcrumbs[i]?.id) ||
        pendingDraftLookup[i] ||
        reservedDraftBreadcrumbNames[i]
          ? null
          : id,
      ),
    [breadcrumbIds, draftsForBreadcrumbs, pendingDraftLookup, reservedDraftBreadcrumbNames],
  )

  const breadcrumbResults = useResources(resourceFetchIds, {subscribed: true})

  const focusedBlock = useMemo(
    () => (routeBlockRef && document.content ? findContentBlock(document.content, routeBlockRef)?.block : null),
    [routeBlockRef, document.content],
  )
  const focusedBlockMentionNames = useMentionNames(
    useMemo(() => (focusedBlock ? extractRefs([{block: focusedBlock, children: []}]) : []), [focusedBlock]),
  )

  const breadcrumbs = useMemo((): BreadcrumbEntry[] | undefined => {
    if (isHomeDoc) return undefined
    const lastIdx = breadcrumbIds.length - 1
    const items: BreadcrumbEntry[] = breadcrumbIds.map((id, i) => {
      const draft = draftsForBreadcrumbs[i]
      const isCurrent = i === lastIdx
      const currentDraftName =
        isCurrent && isUnpublishedDraft
          ? ctx.metadata?.name || (existingDraft ? existingDraft.metadata?.name : undefined)
          : undefined
      const reservedDraftBreadcrumbName = reservedDraftBreadcrumbNames[i]
      const fallbackName = currentDraftName || reservedDraftBreadcrumbName || 'Untitled Document'

      if (draft) {
        const draftIsUnpublished = isDraftPlaceholderPath(id.path, draft.id) || (isCurrent && isUnpublishedDraft)
        const result = breadcrumbResults[i]
        return getDraftBreadcrumbEntry({
          id,
          draft,
          publishedMetadata: isCurrent
            ? ctx.document?.metadata ?? document.metadata
            : result?.data?.type === 'document'
              ? result.data.document.metadata
              : undefined,
          currentMetadata: isCurrent && draftOverlayAllowed ? ctx.metadata : undefined,
          isUnpublishedDraft: draftIsUnpublished,
          isLoading: !isCurrent && !draftIsUnpublished && (!result || result.isLoading || result.isDiscovering),
        })
      }

      if (reservedDraftBreadcrumbName) {
        return {
          id,
          metadata: currentDraftName ? {name: currentDraftName} : {},
          fallbackName,
          isLoading: false,
          isTombstone: false,
          isNotFound: false,
          isError: false,
          isUnpublishedDraft: true,
        }
      }

      if (pendingDraftLookup[i]) {
        return {
          id,
          metadata: {},
          fallbackName,
          isLoading: true,
          isTombstone: false,
          isNotFound: false,
          isError: false,
          isUnpublishedDraft: true,
        }
      }

      const result = breadcrumbResults[i]
      const data = result?.data
      // Fallback: the document machine knows the current doc is an
      // unpublished draft even before the account draft list resolves.
      // Avoids flashing "not found" on the active draft on first paint.
      const showAsUnpublishedDraft = isCurrent && isUnpublishedDraft
      const itemMetadata = isCurrent
        ? currentDraftName
          ? {...metadata, name: currentDraftName}
          : metadata
        : data?.type === 'document'
          ? data.document?.metadata || {}
          : {}
      return {
        id,
        metadata: itemMetadata,
        draftId: showAsUnpublishedDraft && existingDraft ? existingDraft.id : undefined,
        fallbackName,
        isLoading: !result || result?.isDiscovering || result?.isLoading,
        isTombstone: result?.isTombstone,
        isNotFound: !showAsUnpublishedDraft && data?.type === 'not-found' && !result?.isDiscovering,
        isError: result?.isError && !result?.isDiscovering && !result?.isTombstone,
        isUnpublishedDraft: showAsUnpublishedDraft,
      }
    })

    // Append active panel name when not on content/draft view
    const panelLabels: Record<string, string> = {
      comments: 'Comments',
      collaborators: 'People',
      directory: 'Sub documents',
      activity: 'Activity',
      'all-documents': 'All Documents',
      metadata: 'Attributes',
    }
    if (activeView !== 'content' && panelLabels[activeView]) {
      items.push({
        label:
          route.key === 'activity' && activityFilterToSlug(route.filterEventType) === 'citations'
            ? 'Connections'
            : panelLabels[activeView],
      })
    }

    // Append block text when a block is focused
    if (focusedBlock) {
      const routeId = 'id' in route && typeof route.id === 'object' ? route.id : null
      const blockRange = routeId?.blockRange ?? null
      const range =
        blockRange && typeof blockRange.start === 'number' && typeof blockRange.end === 'number'
          ? {start: blockRange.start, end: blockRange.end}
          : undefined
      const text = blockToPlainText(focusedBlock, focusedBlockMentionNames, range)
      const truncated = text.length > 40 ? text.slice(0, 40) + '…' : text
      if (truncated) items.push({label: `"${truncated}"`})
    }

    return items
  }, [
    isHomeDoc,
    breadcrumbIds,
    breadcrumbResults,
    draftsForBreadcrumbs,
    pendingDraftLookup,
    reservedDraftBreadcrumbNames,
    metadata,
    isUnpublishedDraft,
    existingDraft,
    ctx.metadata,
    ctx.document?.metadata,
    document.metadata,
    draftOverlayAllowed,
    activeView,
    focusedBlock,
    focusedBlockMentionNames,
    route,
  ])

  // Mobile panel open state derived from URL panel route
  const mobilePanelOpen = !!panelKey

  const {showSidebars, showCollapsed, sidebarProps, mainContentProps, elementRef, wrapperProps, contentMaxWidth} =
    useDocumentLayout({
      contentWidth: metadata?.contentWidth,
      showSidebars: !isHomeDoc && metadata?.showOutline !== false && activeView === 'content',
    })

  // Fetch author metadata for document header and subscribe for discovery
  const accountsMetadata = useAccountsMetadata(document.authors || [])
  useHackyAuthorsSubscriptions(document.authors || [])
  const authorPayloads: AuthorPayload[] = useMemo(() => {
    return (document.authors || []).map((uid) => {
      const data = accountsMetadata.data[uid]
      if (data) return data
      return {
        id: hmId(uid),
        metadata: null,
      }
    })
  }, [document.authors, accountsMetadata.data])

  // Use document scroll on mobile web, element scroll on desktop/large screens
  // In Electron (IS_DESKTOP), always use element scroll regardless of window width,
  // because the layout uses overflow-hidden containers that prevent document scroll.
  const media = useMedia()
  const isMobile = media.xs && !IS_DESKTOP
  const documentScrollAreaRef = useRef<HTMLDivElement>(null)
  const documentTopSentinelRef = useRef<HTMLDivElement>(null)
  const backToTopButtonRef = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    const root = documentScrollAreaRef.current?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]')
    const sentinel = documentTopSentinelRef.current
    const button = backToTopButtonRef.current
    if (activeView !== 'content' || !root || !sentinel || !button || typeof IntersectionObserver === 'undefined') {
      return
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        if (!entry) return
        const visible = !entry.isIntersecting
        button.dataset.visible = String(visible)
        button.ariaHidden = String(!visible)
        button.tabIndex = visible ? 0 : -1
      },
      {root, rootMargin: `${BACK_TO_TOP_SCROLL_OFFSET}px 0px 0px`},
    )
    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [activeView, docId.id])

  const scrollDocumentToTop = useCallback(() => {
    const viewport = documentScrollAreaRef.current?.querySelector<HTMLElement>('[data-slot="scroll-area-viewport"]')
    viewport?.scrollTo({top: 0, behavior: 'smooth'})
  }, [])

  // Block tools handlers
  const blockCitations = useMemo(() => interactionSummary.data?.blocks || null, [interactionSummary.data?.blocks])
  const copyHmLink = useCopyHmLink()
  // Current origin for gateway-format links (web: site's own URL; desktop: the
  // configured gateway). Used as a fallback when the document has no site URL.
  const {origin: appOrigin, originHomeId, experiments, openRouteNewWindow, openUrl} = useUniversalAppContext()
  const [showCitationFragments, setShowCitationFragments] = useState(true)
  const [citationFragmentClick, setCitationFragmentClick] = useState<CitationFragmentClick | null>(null)
  const citationFragmentHighlights = useMemo(
    () => (showCitationFragments ? normalizeCitationFragmentHighlights(citations.data?.citations) : []),
    [showCitationFragments, citations.data?.citations],
  )

  const handleCitationFragmentSelect = useCallback(
    (citation: CitationFragmentHighlight) => {
      setCitationFragmentClick(null)
      if (citation.sourceType === 'comment' && citation.sourceDocumentId && citation.sourceCommentId) {
        if (route.key !== 'document' && route.key !== 'feed') return
        navigate({
          ...route,
          panel: {
            key: 'comments',
            id: citation.sourceDocumentId,
            openComment: citation.sourceCommentId,
          } as any,
        })
        return
      }

      if (citation.sourceType === 'document' && citation.sourceId) {
        const sourceRoute: NavRoute = {
          key: 'document',
          id: {
            ...citation.sourceId,
            blockRef: citation.sourceBlockId,
            blockRange: citation.sourceBlockId ? {expanded: true} : null,
          },
        }
        if (openRouteNewWindow) {
          openRouteNewWindow(sourceRoute)
        } else {
          const href = routeToUrl(sourceRoute, {hostname: appOrigin, originHomeId}) || hmIdToURL(sourceRoute.id)
          openUrl(href, true)
        }
      }
    },
    [appOrigin, navigate, openRouteNewWindow, openUrl, originHomeId, route],
  )

  const handleCitationFragmentClick = useCallback(
    (click: CitationFragmentClick) => {
      const [singleCitation] = click.citations
      if (singleCitation && click.citations.length === 1) {
        handleCitationFragmentSelect(singleCitation)
        return
      }
      setCitationFragmentClick(click)
    },
    [handleCitationFragmentSelect],
  )
  const handleBlockCitationClick = useCallback(
    (blockId?: string | null) => {
      if (route.key !== 'document' && route.key !== 'feed') return
      navigate({
        ...route,
        id: {
          ...route.id,
          blockRef: blockId || null,
          // Mark the block as expanded so the editor highlight plugin focuses
          // and highlights the whole block (not just a text range).
          blockRange: blockId ? {expanded: true} : null,
        },
        panel: blockId ? getBlockCitationsPanelRoute(route.id, blockId) : undefined,
      })
      if (blockId) scrollToBlock(blockId)
    },
    [route, navigate, scrollToBlock],
  )

  const commentDraftReferences = useCommentDraftReferences()
  const commentQueryClient = useQueryClient()
  const commentClient = useUniversalClient()
  const handleBlockCommentClick = useCallback(
    async (blockId?: string | null, blockRangeInput?: BlockRange | undefined, _startCommentingNow?: boolean) => {
      if (route.key !== 'document' && route.key !== 'feed') return
      if (!blockId) return
      try {
        let resolvedThreadRootId: string | undefined
        if (route.panel?.key === 'comments' && route.panel.openComment) {
          const comments = await commentQueryClient.ensureQueryData(
            queryDocumentComments(commentClient, commentsPanelTarget.docId),
          )
          const thread = getCommentThread(comments.comments, route.panel.openComment)
          if (!thread || thread.root.replyParent) {
            toast.error('The comment thread could not be loaded.')
            return
          }
          resolvedThreadRootId = thread.root.id
        }
        const request = getBlockCommentDraftReference({
          route,
          sourceId: docId,
          resolvedThreadRootId,
          version: document.version,
          blockId,
          blockRange: blockRangeInput,
        })
        commentDraftReferences?.appendReference(request.reference)
        if (request.route !== route) navigate(request.route)
      } catch {
        toast.error('The comment thread could not be loaded.')
      }
    },
    [
      route,
      navigate,
      docId,
      document.version,
      commentDraftReferences?.appendReference,
      commentQueryClient,
      commentClient,
      commentsPanelTarget.docId,
    ],
  )

  // Block select handler (copy block link + navigate to update URL)
  const handleBlockSelect = useCallback(
    (blockId: string, opts?: BlockRangeSelectOptions) => {
      if (route.key !== 'document' && route.key !== 'feed') return
      const blockRange =
        opts && 'start' in opts && 'end' in opts
          ? {start: opts.start, end: opts.end}
          : opts && 'expanded' in opts && opts.expanded
            ? {expanded: true}
            : null
      const blockRoute = {
        ...route,
        id: {
          ...route.id,
          blockRef: blockId,
          blockRange,
        },
      }
      const shouldCopy = opts?.copyToClipboard !== false
      if (blockId && shouldCopy) {
        // Pin to the published version only if the block actually exists there.
        // Newly-added draft blocks aren't in any published version yet — emit
        // a version-less, latest-resolving link so the URL stays valid once
        // the next publish lands.
        const publishedVersionCandidate = publishedVersion ?? document.version
        const existsInPublished = !!findContentBlock(document.content ?? [], blockId)
        const versionForLink = existsInPublished ? publishedVersionCandidate : null
        copyHmLink({
          id: {
            ...docId,
            version: versionForLink ?? null,
            blockRef: blockId,
            blockRange,
            hostname: siteUrl ?? docId.hostname ?? null,
            latest: existsInPublished ? null : true,
          },
          // Fall back to the app origin for gateway-format URLs so copied
          // links on web point back to this deployment (instead of the
          // default `hyper.media` gateway) when no site URL is set.
          gatewayUrl: appOrigin ?? undefined,
        })
      }
      // Always scroll + navigate so the URL updates and the target block gets
      // visually focused/highlighted — even on copy clicks.
      scrollToBlock(blockId)
      navigate(blockRoute)
    },
    [
      route,
      navigate,
      scrollToBlock,
      docId,
      document.version,
      document.content,
      siteUrl,
      publishedVersion,
      copyHmLink,
      appOrigin,
    ],
  )

  const handleTextSelection = useCallback(() => {
    if (route.key !== 'document' && route.key !== 'feed') return
    if (!route.id.blockRef && !route.id.blockRange) return

    replaceRoute({
      ...route,
      id: {
        ...route.id,
        blockRef: null,
        blockRange: null,
      },
    })
  }, [route, replaceRoute])

  // Activity filter change handler (main page)
  const handleMainActivityFilterChange = (filter: {filterEventType?: string[]}) => {
    if (route.key === 'activity') {
      navigate({
        ...route,
        filterEventType: filter.filterEventType,
      })
    }
  }

  // Options dropdown: fully controlled by platform wrappers.
  const inspectMenuItem = useMemo<MenuItemType | null>(() => {
    if (!experiments?.developerTools) return null
    if (route.key === 'inspect') return null
    const inspectDocId = {...docId, blockRef: null, blockRange: null}
    return {
      key: 'inspect',
      label: 'Inspect Document',
      icon: <Search className="size-4" />,
      onClick: () => {
        navigate(createInspectNavRoute(inspectDocId))
      },
    }
  }, [docId, navigate, route.key, experiments?.developerTools])
  const documentOptionsMenuItem = useMemo<MenuItemType | null>(() => {
    // An agent has its own Settings tab; the document's settings panel is not offered.
    if (!canEditCurrentRoute || isAgentDocument) return null
    return {
      key: 'options',
      label: 'Document Settings',
      icon: <FilePen className="size-4" />,
      onClick: () => {
        const newPanel = panelKey === 'options' ? null : {key: 'options' as const}
        replaceRoute({...route, panel: newPanel} as any)
      },
    }
  }, [canEditCurrentRoute, isAgentDocument, panelKey, route, replaceRoute])
  // On the Attributes page, a field outside the attributes schema is added from the options menu.
  const addRawFieldMenuItem = useMemo<MenuItemType | null>(() => {
    if (!canEditCurrentRoute || route.key !== 'metadata') return null
    return {
      key: 'add-raw-field',
      label: 'Add Raw Field',
      icon: <Plus className="size-4" />,
      onClick: () => replaceRoute({...route, addRawField: true}),
    }
  }, [canEditCurrentRoute, route, replaceRoute])
  const citationFragmentToggleMenuItem = useMemo<MenuItemType>(
    () => ({
      key: 'citation-fragments-toggle',
      label: showCitationFragments ? 'Hide fragment citations' : 'Show fragment citations',
      icon: <Quote className="size-4" />,
      onClick: () => {
        setShowCitationFragments((value) => !value)
        setCitationFragmentClick(null)
      },
    }),
    [showCitationFragments],
  )

  const convertToDocumentMenuItem = useMemo<MenuItemType | null>(() => {
    if (!isCollection || !canEditCurrentRoute) return null
    return {
      key: 'convert-to-document',
      label: 'Convert to Document',
      icon: <FileText className="size-4" />,
      onClick: () => setPendingDocumentConversion('document'),
    }
  }, [canEditCurrentRoute, isCollection])

  const convertToCollectionMenuItem = useMemo<MenuItemType | null>(() => {
    // An agent's document is the agent, never a collection.
    if (isCollection || !canEditCurrentRoute || isAgentDocument) return null
    return {
      key: 'convert-to-collection',
      label: 'Convert to Collection',
      icon: <Grid3X3 className="size-4" />,
      onClick: () => setPendingDocumentConversion('collection'),
    }
  }, [canEditCurrentRoute, isAgentDocument, isCollection])

  // The schema actions (New Document / New Collection / Extend / New Raw Value) ride in the
  // regular options menu; the list is empty unless the document defines a schema.
  const schemaMenuItems = useSchemaMenuItems(document?.metadata, {
    docUrl: docId.id,
    onExtendSchema,
    onNewTypedDocument,
    onNewTypedCollection,
  })
  const allMenuItems = useMemo(() => {
    let unorderedItems: MenuItemType[] = [...(optionsMenuItems ?? extraMenuItems ?? [])]
    if (documentPage) {
      unorderedItems.push(
        ...getDocumentPagePanelMenuItems(docId, (panel) => replaceRoute({...route, panel} as NavRoute)),
      )
      if (editPageParameters)
        unorderedItems.push({
          key: 'page-parameters',
          label: 'Page parameters',
          icon: <SlidersHorizontal className="size-4" />,
          onClick: editPageParameters,
        })
      if (managePagePermissions)
        unorderedItems.push({
          key: 'page-permissions',
          label: 'Page permissions',
          icon: <ShieldCheck className="size-4" />,
          onClick: managePagePermissions,
        })
    } else unorderedItems.push(citationFragmentToggleMenuItem)
    if (inspectMenuItem) unorderedItems.push(inspectMenuItem)
    if (documentOptionsMenuItem) unorderedItems.push(documentOptionsMenuItem)
    // An agent's tab bar has no Attributes tab; the options menu opens that panel instead.
    if (isAgentDocument && !documentPage)
      unorderedItems.push({
        key: 'app-attributes',
        label: 'Attributes',
        icon: <Info className="size-4" />,
        onClick: () =>
          replaceRoute({
            ...route,
            panel: {key: 'metadata', id: {...docId, blockRef: null, blockRange: null}},
          } as NavRoute),
      })
    if (addRawFieldMenuItem) unorderedItems.push(addRawFieldMenuItem)
    unorderedItems.push(...schemaMenuItems)
    if (convertToCollectionMenuItem) unorderedItems.push(convertToCollectionMenuItem)
    if (convertToDocumentMenuItem) unorderedItems.push(convertToDocumentMenuItem)
    // Drop share/copy-link entries while the doc is an unpublished draft —
    // its URL won't resolve for anyone else, so any "share" action is a footgun.
    if (isUnpublishedDraft) {
      const drop = (key: string) => key === 'copy-link' || key.startsWith('copy-') || key === 'share'
      unorderedItems = unorderedItems.filter((item) => !drop(item.key))
    }
    return orderDocumentMenuItems(unorderedItems)
  }, [
    documentPage,
    docId,
    route,
    replaceRoute,
    managePagePermissions,
    editPageParameters,
    optionsMenuItems,
    extraMenuItems,
    citationFragmentToggleMenuItem,
    inspectMenuItem,
    documentOptionsMenuItem,
    isAgentDocument,
    addRawFieldMenuItem,
    convertToCollectionMenuItem,
    convertToDocumentMenuItem,
    schemaMenuItems,
    isUnpublishedDraft,
  ])

  const hasOptions = allMenuItems.length > 0
  const actionButtons = hasOptions ? <OptionsDropdown menuItems={allMenuItems} align="end" side="bottom" /> : null
  const documentConversionDialog = (
    <AlertDialog
      open={pendingDocumentConversion !== null}
      onOpenChange={(open) => !open && setPendingDocumentConversion(null)}
    >
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Convert to {pendingDocumentConversion === 'collection' ? 'Collection' : 'Document'}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            {pendingDocumentConversion === 'collection'
              ? 'This will remove all document content and replace it with a query block.'
              : 'This will remove the collection query block and leave an empty document.'}
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction
            variant="default"
            onClick={() => {
              if (!pendingDocumentConversion) return
              console.info('[Collection draft debug] document conversion confirmed', {
                draftId: ctx.draftId,
                conversion: pendingDocumentConversion,
              })
              send({
                type:
                  pendingDocumentConversion === 'collection'
                    ? 'collection.convertToCollection'
                    : 'collection.convertToDocument',
              })
              setPendingDocumentConversion(null)
            }}
          >
            Convert
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  )
  const documentContentAction = getDocumentContentAction({
    activeView,
    isEditing,
    hasDraft: draftOverlayAllowed && ctx.draftId !== null,
    editingFloatingActions: draftOverlayAllowed ? editingFloatingActions : undefined,
    draftActions: draftOverlayAllowed ? draftActions : undefined,
    actionButtons,
    allMenuItems,
  })
  // A document that DEFINES a type (carries a `schemaDefinition`) gets header
  // actions: New Document / New Collection typed by it (or Create, for a signed type).
  // Use draft-merged metadata so an unpublished schemaDefinition still surfaces
  // (same source the Attributes tab reads).
  const headerMetadata = {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
  const schemaDocActions = schemaDefinitionCid(headerMetadata) ? (
    <SchemaDocumentHeaderActions
      metadata={headerMetadata}
      docUrl={docId.id}
      onNewTypedDocument={onNewTypedDocument}
      onNewTypedCollection={onNewTypedCollection}
    />
  ) : null
  const topBarActions =
    schemaDocActions || documentContentAction ? (
      <>
        {schemaDocActions}
        {documentContentAction}
      </>
    ) : null
  const floatingButtonsAction =
    activeView === 'content' && !documentPage && !documentContentAction ? floatingButtons : null

  // The bar always states where you are, so a home document is its own single crumb.
  const spaceBreadcrumbs = breadcrumbs ?? [{id: hmId(docId.uid, {latest: true}), metadata}]
  const topBarBreadcrumbs: BreadcrumbEntry[] =
    activeView === 'explore' ? [...spaceBreadcrumbs, {label: 'Explore'}] : spaceBreadcrumbs
  const documentTopBar = (
    <DocumentTopBar
      breadcrumbs={topBarBreadcrumbs}
      status={
        !isHomeDoc && (headerVisibility === 'PRIVATE' || versionHeadCount > 1) ? (
          <>
            {headerVisibility === 'PRIVATE' ? <PrivateBadge size="sm" /> : null}
            <MergedBadge count={versionHeadCount} size="sm" />
          </>
        ) : null
      }
      actions={topBarActions}
      isMobile={isMobile}
      collapseAction={
        expandedPanel
          ? {inlineButton: inlineCollapseButton, title: expandedPanel.title, onCollapse: collapseExpandedPanel}
          : undefined
      }
    />
  )

  // Main page content (used in both mobile and desktop layouts)
  const mainPageContent = documentPage ? (
    <div data-document-page-main className="min-h-0 flex-1">
      {documentConversionDialog}
      <DocumentPageView
        docId={docId}
        document={document}
        cid={documentPage}
        inputParams={route.key === 'document' ? route.id.pageParams : undefined}
        onInputNavigate={(pageParams, mode) => {
          const next = {...route, id: {...docId, pageParams}} as NavRoute
          if (mode === 'replace') replaceRoute(next)
          else navigate(next)
        }}
        perspectiveAccountUid={perspectiveAccountUid}
        onAccountAccessChange={onPageAccessChange}
        onParametersChange={onPageParametersChange}
        fill
      />
    </div>
  ) : (
    <div
      className={cn(
        'flex flex-col',
        isAgentView
          ? 'h-full min-h-0'
          : pageFooter
            ? 'min-h-[calc(100dvh-var(--site-header-live-h,var(--site-header-default-h,60px))-var(--hm-host-banner-h,0px))]'
            : 'min-h-full',
      )}
    >
      {documentConversionDialog}
      {!isAgentDocument && (
        <DocumentCover
          cover={metadata?.cover}
          onRemove={canEditCurrentRoute && metadata?.cover ? removeCover : undefined}
          onChangeCover={canEditCurrentRoute && metadata?.cover && fileUpload ? changeCover : undefined}
        />
      )}
      {isHomeDoc && canEditCurrentRoute && activeView === 'content' ? (
        <HomeDocumentMetadataControls metadata={metadata as any} fileUpload={fileUpload} />
      ) : null}

      {!isMobile ? (
        <div
          {...wrapperProps}
          className={cn(
            wrapperProps.className,
            'flex-none',
            !showSidebars && 'justify-center',
            fullWidthHeader && '!block w-full',
          )}
          style={fullWidthHeader ? {...wrapperProps.style, maxWidth: undefined} : wrapperProps.style}
        >
          {showSidebars && !isCollection && <div {...sidebarProps} className={cn(sidebarProps.className, '!h-auto')} />}
          <div
            {...mainContentProps}
            className={cn(mainContentProps.className, 'flex flex-col', fullWidthHeader && '!w-full !max-w-none')}
            style={fullWidthHeader ? {...mainContentProps.style, maxWidth: undefined} : mainContentProps.style}
          >
            {showMembersFacepile && (
              <div className="pt-4">
                <MembersFacepile members={siteMembers.members} siteId={siteId} />
              </div>
            )}
            {!isHomeDoc &&
              !isAgentDocument &&
              (canEditCurrentRoute ? (
                <EditableDocumentHeader
                  docId={docId}
                  docMetadata={metadata}
                  authors={authorPayloads}
                  updateTime={document.updateTime}
                  visibility={headerVisibility}
                  version={document.version}
                  fileUpload={fileUpload}
                  flushByline={isCollection}
                />
              ) : (
                <DocumentHeader
                  docId={docId}
                  docMetadata={metadata}
                  authors={authorPayloads}
                  updateTime={document.updateTime}
                  visibility={headerVisibility}
                  version={document.version}
                  flushByline={isCollection}
                />
              ))}
          </div>
          {showSidebars && !isCollection && <div {...sidebarProps} className={cn(sidebarProps.className, '!h-auto')} />}
        </div>
      ) : (
        <div
          className={cn('mx-auto flex w-full flex-col', !expandedPanel && 'px-4')}
          style={{maxWidth: fullWidthHeader ? undefined : contentMaxWidth}}
        >
          {showMembersFacepile && (
            <div className="pt-4">
              <MembersFacepile members={siteMembers.members} siteId={siteId} />
            </div>
          )}
          {!isHomeDoc &&
            !isAgentDocument &&
            (canEditCurrentRoute ? (
              <EditableDocumentHeader
                docId={docId}
                docMetadata={metadata}
                authors={authorPayloads}
                updateTime={document.updateTime}
                visibility={headerVisibility}
                version={document.version}
                fileUpload={fileUpload}
                flushByline={isCollection}
              />
            ) : (
              <DocumentHeader
                docId={docId}
                docMetadata={metadata}
                authors={authorPayloads}
                updateTime={document.updateTime}
                visibility={headerVisibility}
                version={document.version}
                flushByline={isCollection}
              />
            ))}
        </div>
      )}
      {/* Document tools scroll with the page and are hidden when showActivity is false,
          except on a mounted agent document, whose views live behind these tabs. */}
      {expandedPanel ? (
        <div className="pt-4 pb-2">
          <PageLayout contentMaxWidth={contentMaxWidth} contentClassName="px-4">
            <div className="border-border flex items-center justify-between gap-2 border-b pb-4">
              <Text weight="semibold" size="lg">
                {expandedPanel.title}
              </Text>
              <Tooltip content="Collapse">
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Collapse ${expandedPanel.title}`}
                  ref={setInlineCollapseButton}
                  onClick={collapseExpandedPanel}
                >
                  <Minimize2 className="size-4" />
                </Button>
              </Tooltip>
            </div>
          </PageLayout>
        </div>
      ) : (
        showDocumentChrome && (
          <AgentDocumentView document={document} route={{key: 'document', id: docId}}>
            {(hasAgent) =>
              (showActivity || hasAgent) && (
                <div className="px-5 py-1">
                  <DocumentTools
                    id={docId}
                    hasAgent={hasAgent}
                    agentHome={isAgentDocument}
                    activeAgentView={route.key === 'document' ? route.agentView : undefined}
                    activeTab={
                      activeView === 'activity' &&
                      activityFilterToSlug(route.key === 'activity' ? route.filterEventType : undefined) === 'citations'
                        ? 'citations'
                        : activeView === 'activity' ||
                            activeView === 'directory' ||
                            activeView === 'site-profile' ||
                            activeView === 'all-documents'
                          ? undefined
                          : activeView
                    }
                    currentPanel={panelRoute}
                    currentRoute={route}
                    commentsCount={interactionSummary.data?.comments || 0}
                    citationsCount={connectionCount}
                    collabsCount={peopleCount}
                    collaborators={collaborators.data}
                    metadataCount={countCustomMetadataFields(metadata)}
                    schemaCid={schemaDefinitionCid(metadata)}
                    layoutProps={
                      isMobile || isCollection
                        ? undefined
                        : {
                            wrapperProps,
                            sidebarProps,
                            mainContentProps,
                            showSidebars,
                          }
                    }
                  />
                </div>
              )
            }
          </AgentDocumentView>
        )
      )}

      {/* Main content based on activeView */}
      <div
        className={cn(
          'flex-1',
          isAgentView && 'flex min-h-0 flex-col',
          activeView !== 'content' && activeView !== 'comments' && 'pb-60',
          isMobile && !expandedPanel && 'px-4',
        )}
      >
        {route.key === 'document' && (route.agentView || isAgentDocument) ? (
          <AgentDocumentView document={document} route={route} contentMaxWidth={contentMaxWidth} />
        ) : (
          <DocumentViewContent
            activeView={activeView}
            collection={
              isCollection ? (
                <DocumentCollection docId={docId} queryBlock={collectionQueryBlock} canEdit={canEditCurrentRoute} />
              ) : undefined
            }
          >
            <CommentsFooterContext.Provider value={mainCommentsFooter}>
              <MainContent
                docId={docId}
                resourceId={'id' in route && typeof route.id === 'object' ? route.id : docId}
                document={document}
                activeView={activeView}
                contentMaxWidth={contentMaxWidth}
                wrapperProps={wrapperProps}
                sidebarProps={sidebarProps}
                mainContentProps={mainContentProps}
                showSidebars={showSidebars}
                showCollapsed={showCollapsed}
                discussionsParams={discussionsParams}
                suppressCommentEditor={suppressMainCommentEditor}
                activityFilterEventType={route.key === 'activity' ? route.filterEventType : undefined}
                onActivityFilterChange={handleMainActivityFilterChange}
                blockCitations={blockCitations}
                citationFragmentHighlights={citationFragmentHighlights}
                onCitationFragmentClick={handleCitationFragmentClick}
                onBlockCitationClick={handleBlockCitationClick}
                onBlockCommentClick={handleBlockCommentClick}
                onBlockSelect={handleBlockSelect}
                onTextSelection={handleTextSelection}
                isUnpublishedDraft={isUnpublishedDraft}
                isBlockInPublishedVersion={isBlockInPublishedVersion}
                CommentEditor={CommentEditor}
                directory={directory.data}
                siteUrl={siteUrl}
                inlineCards={inlineCards}
                inlineInsert={inlineInsert}
                DocumentContentComponent={DocumentContentComponent}
                onEditorReady={handleEditorReadyWrapped}
                existingDraftContent={effectiveContent}
                existingDraftCursorPosition={existingDraftCursorPosition}
                ssrContentHTML={ssrContentHTML}
                perspectiveAccountUid={perspectiveAccountUid}
                linkExtensionOptions={linkExtensionOptions}
                fileUpload={fileUpload}
                draftVersionEntry={draftVersionEntry}
              />
            </CommentsFooterContext.Provider>
          </DocumentViewContent>
        )}
        {citationFragmentClick ? (
          <CitationFragmentPopover
            click={citationFragmentClick}
            onClose={() => setCitationFragmentClick(null)}
            onCitationSelect={handleCitationFragmentSelect}
          />
        ) : null}
      </div>
      {pageFooter ? <div className="mt-auto">{pageFooter}</div> : null}
    </div>
  )

  // Close panel handler
  const handlePanelClose = () => {
    if ('panel' in route) {
      navigate({...route, panel: null})
    }
  }

  const handleCommentsExpand = () => {
    if (panelRoute?.key !== 'comments') return
    navigate({...panelRoute, id: commentsPanelTarget.docId, panel: null})
  }

  // Activity filter change handler (panel)
  const handleFilterChange = (filter: {filterEventType?: string[]}) => {
    if ((route.key === 'document' || route.key === 'feed') && route.panel?.key === 'activity') {
      navigate({
        ...route,
        panel: {...route.panel, filterEventType: filter.filterEventType},
      })
    }
  }

  // The space Activity panel brings its own header. Switching tabs replaces the history entry.
  const activityPanelHeader =
    panelRoute?.key === 'feed' ? (
      <ActivityHeader
        tab={panelRoute.tab}
        onTabChange={(tab) => replaceRoute({...route, panel: {...panelRoute, tab}} as NavRoute)}
        onExpand={
          isMobile
            ? undefined
            : () => navigate({key: 'feed', id: hmId(panelRoute.id?.uid ?? docId.uid), tab: panelRoute.tab})
        }
        onClose={handlePanelClose}
      />
    ) : undefined

  const documentPanelHeader = isConnectionsPanel ? (
    <PanelHeader
      title="Connections"
      count={
        groupDocumentConnections(panelRoute.id ?? docId, panelCitations.data?.citations, panelRoute.targetBlockId)
          .length
      }
      onClose={handlePanelClose}
      onExpand={
        isMobile
          ? undefined
          : () =>
              navigate({
                key: 'activity',
                id: {...(panelRoute.id ?? docId), blockRef: panelRoute.targetBlockId || null},
                filterEventType: activitySlugToFilter('citations'),
              })
      }
    />
  ) : panelRoute?.key === 'metadata' || panelRoute?.key === 'collaborators' ? (
    <PanelHeader
      title={panelRoute.key === 'metadata' ? 'Attributes' : 'People'}
      onClose={handlePanelClose}
      onExpand={isMobile ? undefined : () => navigate({key: panelRoute.key, id: panelRoute.id ?? docId})}
    />
  ) : undefined

  // Both layouts use the same panel content and reply navigation.
  const panelContent = panelRoute ? (
    <PanelContentRenderer
      panelRoute={panelRoute}
      docId={docId}
      document={document}
      contentMaxWidth={contentMaxWidth}
      CommentEditor={CommentEditor}
      siteUrl={siteUrl}
      fileUpload={fileUpload}
      draftVersionEntry={draftVersionEntry}
      autoFocusComments={!isMobile}
    />
  ) : null

  // Mobile: use document scroll with bottom bar and panel sheet
  if (isMobile) {
    return (
      <>
        <div
          className={cn('relative flex flex-1 flex-col', fixedHeightMain ? 'h-full min-h-0 overflow-hidden' : 'pb-20')}
          ref={elementRef}
        >
          {documentTopBar}
          {mainPageContent}
          {activeView === 'comments' && <div ref={setMainCommentsFooter} className="sticky bottom-20 z-10 shrink-0" />}
          {floatingButtonsAction}
        </div>

        {mobilePanelOpen && (
          <MobilePanelSheet
            isOpen={mobilePanelOpen}
            title={isConnectionsPanel ? 'Connections' : getPanelTitle(panelKey)}
            onClose={handlePanelClose}
            header={documentPanelHeader ?? activityPanelHeader}
            resizable
            scrollContent={panelKey !== 'comments' && panelKey !== 'feed'}
            onBack={
              panelRoute?.key === 'comments' && panelRoute.openComment && 'panel' in route
                ? () => replaceRoute({...route, panel: {key: 'comments', id: commentsPanelTarget.docId}})
                : undefined
            }
            count={panelKey === 'comments' ? commentsPanelSummary.data?.comments : undefined}
          >
            {panelContent}
          </MobilePanelSheet>
        )}
      </>
    )
  }

  return (
    <div className="relative flex flex-1 flex-col overflow-hidden" ref={elementRef}>
      <ActivityPanelPortal active={panelKey === 'feed'}>
        {activityPanelHeader}
        <ScrollArea className="min-h-0 flex-1">{panelContent}</ScrollArea>
      </ActivityPanelPortal>
      <PanelLayout
        panelKey={panelKey === 'feed' ? null : panelKey}
        panelContent={
          panelKey === 'feed' ? null : panelKey === 'comments' ? (
            panelContent
          ) : (
            <ScrollArea className="flex-1">{panelContent}</ScrollArea>
          )
        }
        onPanelClose={handlePanelClose}
        commentsCount={commentsPanelSummary.data?.comments}
        onCommentsExpand={handleCommentsExpand}
        onCommentsBack={
          panelRoute?.key === 'comments' && panelRoute.openComment && 'panel' in route
            ? () => replaceRoute({...route, panel: {key: 'comments', id: commentsPanelTarget.docId}})
            : undefined
        }
        isVersionsPanel={isDocumentVersionsPanelRoute(panelRoute)}
        isCitationsPanel={
          panelRoute?.key === 'activity' &&
          (!!panelRoute.targetBlockId || activityFilterToSlug(panelRoute.filterEventType) === 'citations')
        }
        filterEventType={panelRoute?.key === 'activity' ? panelRoute.filterEventType : undefined}
        onFilterChange={handleFilterChange}
        header={documentPanelHeader ?? activityPanelHeader}
      >
        <div className="relative flex h-full min-h-0 flex-col">
          {documentTopBar}
          {fixedHeightMain ? (
            mainPageContent
          ) : (
            <ScrollArea
              ref={documentScrollAreaRef}
              id="scroll-page-wrapper"
              className="min-h-0 flex-1"
              viewportClassName="scroll-pt-4 [&>div]:!block [&>div]:flex [&>div]:min-h-full [&>div]:flex-col"
              fillViewportContent
            >
              <div ref={documentTopSentinelRef} className="h-px" aria-hidden="true" />
              {mainPageContent}
            </ScrollArea>
          )}
          {activeView === 'comments' && <div ref={setMainCommentsFooter} className="shrink-0" />}
          {activeView === 'content' && !fixedHeightMain ? (
            <Button
              ref={backToTopButtonRef}
              type="button"
              variant="inverse"
              size="sm"
              aria-hidden="true"
              tabIndex={-1}
              data-visible="false"
              className="pointer-events-none invisible absolute bottom-4 left-1/2 z-20 -translate-x-1/2 translate-y-2 opacity-0 shadow-sm transition-[opacity,transform] duration-200 data-[visible=true]:pointer-events-auto data-[visible=true]:visible data-[visible=true]:translate-y-0 data-[visible=true]:opacity-100 motion-reduce:transition-none"
              onClick={scrollDocumentToTop}
            >
              <ArrowUp />
              Back to Top
            </Button>
          ) : null}
        </div>
      </PanelLayout>
    </div>
  )
}

/**
 * Editable document header shown when in editing mode.
 * Renders the same authors/date via DocumentHeader but replaces
 * the static title and summary with editable textareas that send `change`
 * events to the document machine.
 */
function EditableDocumentHeader({
  docId,
  docMetadata,
  authors,
  updateTime,
  visibility,
  version,
  fileUpload,
  flushByline,
  titleAccessory,
}: {
  docId: UnpackedHypermediaId
  docMetadata: HMDocument['metadata']
  authors: AuthorPayload[]
  updateTime: HMDocument['updateTime']
  visibility?: string
  version?: HMDocument['version'] | null
  fileUpload?: (file: File) => Promise<string>
  flushByline?: boolean
  titleAccessory?: React.ReactNode
}) {
  const ctx = useDocumentSelector(selectContext)
  const isEditing = useDocumentSelector(selectIsEditing)
  const focusTitleOnMount = useDocumentSelector(selectShouldFocusDraftTitle)
  const send = useDocumentSend()
  const [summaryRequested, setSummaryRequested] = useState(false)

  // Use machine context metadata if it has been changed, otherwise fall back to document metadata
  const name = ctx.metadata?.name ?? docMetadata?.name ?? ''
  const summary = ctx.metadata?.summary ?? docMetadata?.summary ?? ''
  const metadata = {...(docMetadata || {}), ...ctx.metadata}

  return (
    <DocumentHeader
      docId={docId}
      docMetadata={docMetadata}
      authors={authors}
      updateTime={updateTime}
      visibility={visibility as any}
      version={version}
      flushByline={flushByline}
      titleAccessory={titleAccessory}
      mobileBylineAction={
        <DocumentMetadataAffordanceButtons
          metadata={metadata}
          visible
          mobileOnly
          fileUpload={fileUpload}
          onBeforeMetadataChange={() => {
            if (!isEditing) send({type: 'edit.start'})
          }}
          onMetadata={(metadata) => {
            send({type: 'change', metadata})
          }}
          onRequestSummary={() => {
            setSummaryRequested(true)
          }}
        />
      }
      showTitle={false}
      onRemoveIcon={
        metadata.icon
          ? () => {
              if (!isEditing) send({type: 'edit.start'})
              send({type: 'change', metadata: {icon: ''}})
            }
          : undefined
      }
    >
      <EditableDocumentMetadataFields
        name={name}
        summary={summary}
        metadata={metadata}
        fileUpload={fileUpload}
        focusTitleOnMount={focusTitleOnMount}
        onBeginEdit={() => {
          if (!isEditing) send({type: 'edit.start'})
        }}
        onMetadata={(metadata) => {
          send({type: 'change', metadata})
        }}
        onCancelEdit={() => {
          if (isEditing) {
            send({type: 'edit.cancel'})
          }
        }}
        onSummaryEnter={() => {
          send({type: 'edit.start', cursorPosition: 'end'})
        }}
        summaryRequested={summaryRequested}
        onSummaryRequestedChange={setSummaryRequested}
      />
    </DocumentHeader>
  )
}

// Renders panel content based on panel type
function PanelContentRenderer({
  panelRoute,
  docId,
  document,
  contentMaxWidth,
  CommentEditor,
  siteUrl,
  fileUpload,
  draftVersionEntry,
  autoFocusComments = true,
}: {
  panelRoute: DocumentPanelRoute
  docId: UnpackedHypermediaId
  document: HMDocument
  contentMaxWidth: number
  CommentEditor?: React.ComponentType<CommentEditorProps>
  siteUrl?: string
  fileUpload?: (file: File) => Promise<string>
  draftVersionEntry?: DraftVersionEntry
  autoFocusComments?: boolean
}) {
  switch (panelRoute.key) {
    case 'options':
      return <DocumentOptionsPanel docId={docId} fileUpload={fileUpload} />
    case 'metadata':
      return (
        <div className="px-4">
          <DocumentMetadataPage docId={docId} document={document} fileUpload={fileUpload} />
        </div>
      )
    case 'activity':
      if (isDocumentVersionsPanelRoute(panelRoute)) {
        return (
          <DocumentVersionsPanel size="sm" docId={docId} targetDomain={siteUrl} draftVersionEntry={draftVersionEntry} />
        )
      }
      if (panelRoute.targetBlockId || activityFilterToSlug(panelRoute.filterEventType) === 'citations') {
        return <ConnectionsPanel docId={panelRoute.id ?? docId} targetBlockId={panelRoute.targetBlockId} />
      }
      return (
        <Feed
          size="sm"
          filterResource={docId.id}
          filterEventType={panelRoute.filterEventType}
          targetDomain={siteUrl}
          draftVersionEntry={draftVersionEntry}
          targetBlockId={panelRoute.targetBlockId}
        />
      )
    case 'comments':
      const commentsPanelTarget = getCommentsPanelTarget(docId, panelRoute)
      return (
        <CommentsPanelContent
          docId={commentsPanelTarget.docId}
          openComment={commentsPanelTarget.openComment}
          panelRoute={panelRoute}
          contentMaxWidth={contentMaxWidth}
          targetDomain={siteUrl}
          CommentEditor={CommentEditor}
          autoFocus={autoFocusComments}
        />
      )
    case 'directory':
      return <DirectoryPageContent docId={docId} showTitle={false} contentMaxWidth={contentMaxWidth} />
    case 'collaborators':
      return (
        <div className="p-4">
          <CollaboratorsPage docId={panelRoute.id ?? docId} />
        </div>
      )
    case 'feed':
      return (
        <ActivityContent spaceId={hmId(panelRoute.id?.uid ?? docId.uid)} tab={panelRoute.tab} targetDomain={siteUrl} />
      )

    default:
      return null
  }
}

function CommentsPanelContent({
  docId,
  openComment,
  panelRoute,
  contentMaxWidth,
  targetDomain,
  CommentEditor,
  autoFocus = true,
}: {
  docId: UnpackedHypermediaId
  openComment?: string
  panelRoute: Extract<DocumentPanelRoute, {key: 'comments'}>
  contentMaxWidth: number
  targetDomain?: string
  CommentEditor?: React.ComponentType<CommentEditorProps>
  autoFocus?: boolean
}) {
  const commentsContext = useCommentsServiceContext()
  const route = useNavRoute()
  const navigate = useNavigate()
  const replaceRoute = useNavigate('replace')

  const onReplyClick = useCallback(
    (comment: HMComment) => {
      if (!('panel' in route)) return
      const navigateThread = openComment === (comment.threadRoot || comment.id) ? replaceRoute : navigate
      navigateThread({
        ...route,
        panel: getCommentReplyPanelRoute({
          docId,
          comment,
          isReplying: true,
        }),
      } as NavRoute)
    },
    [docId, navigate, openComment, replaceRoute, route],
  )

  const onReplyCountClick = useCallback(
    (comment: HMComment) => {
      if (!('panel' in route)) return
      const navigateThread = openComment === (comment.threadRoot || comment.id) ? replaceRoute : navigate
      navigateThread({
        ...route,
        panel: getCommentReplyPanelRoute({
          docId,
          comment,
        }),
      } as NavRoute)
    },
    [docId, navigate, openComment, replaceRoute, route],
  )

  return (
    <CommentsProvider {...commentsContext} onReplyClick={onReplyClick} onReplyCountClick={onReplyCountClick}>
      <DiscussionsPageContent
        isPanel
        docId={docId}
        showTitle={false}
        contentMaxWidth={contentMaxWidth}
        targetDomain={targetDomain}
        openComment={openComment}
        replyCommentId={panelRoute.replyCommentId}
        targetBlockId={panelRoute.targetBlockId}
        blockId={panelRoute.blockId}
        blockRange={panelRoute.blockRange}
        commentEditor={
          CommentEditor ? (
            <CommentEditor
              key={getCommentEditorRouteKey({
                openComment,
                targetBlockId: panelRoute.targetBlockId,
                blockRange: panelRoute.blockRange,
              })}
              docId={docId}
              quotingBlockId={panelRoute.targetBlockId}
              quotingRange={extractQuotingRange(panelRoute.blockRange)}
              commentId={openComment}
              isReplying={panelRoute.isReplying ?? !!openComment}
              replyCommentVersion={panelRoute.replyCommentVersion}
              rootReplyCommentVersion={panelRoute.rootReplyCommentVersion}
              focusOnMount={autoFocus && !!panelRoute.isReplying}
            />
          ) : undefined
        }
      />
    </CommentsProvider>
  )
}

function DocumentOptionsPanel({
  docId,
  fileUpload,
}: {
  docId: UnpackedHypermediaId
  fileUpload?: (file: File) => Promise<string>
}) {
  const ctx = useDocumentSelector(selectContext)
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  const homeDraftOverride = useIsHomeDraftOverride()
  const isHomeDoc = homeDraftOverride ?? !docId.path?.length

  const metadata = {...(ctx.document?.metadata || {}), ...ctx.metadata}
  // draftId may not exist yet when the panel is opened in read mode. Fall back
  // to docId.id so form field HTML ids remain stable and unique.
  const formId = ctx.draftId ?? docId.id

  return (
    <OptionsPanel
      draftId={formId}
      metadata={metadata as any}
      isHomeDoc={isHomeDoc}
      fileUpload={fileUpload}
      onMetadata={(newMetadata) => {
        if (!newMetadata) return
        beginEditIfNeeded()
        send({type: 'change', metadata: newMetadata})
      }}
    />
  )
}

function HomeDocumentMetadataControls({
  metadata,
  fileUpload,
}: {
  metadata: HMDocument['metadata']
  fileUpload?: (file: File) => Promise<string>
}) {
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()

  return (
    <HomeDocumentMetadataAffordanceBar
      metadata={metadata}
      fileUpload={fileUpload}
      onBeginEdit={beginEditIfNeeded}
      onMetadata={(patch) => {
        beginEditIfNeeded()
        send({type: 'change', metadata: patch})
      }}
    />
  )
}

/** Metadata view wired to the document machine: edits stage into the draft
 * and publish through the standard publish flow. */
/**
 * The schema a document defines, shown above its body: a published schema
 * (`schemaDefinition`) is browsed in place; a draft's working schema (`schemaDraft`) is edited in
 * place by anyone who can edit the document. Quiet framing — the document's own header carries the
 * name and description, and the secondary parts (dependencies) start collapsed.
 */
function DocumentSchemaSection({document, canEdit}: {document: HMDocument; canEdit: boolean}) {
  const ctx = useDocumentSelector(selectContext)
  const metadata = {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
  const hasDraft = !!ctx.schemaDraft && canEdit
  if (!hasDraft && !schemaDefinitionCid(metadata)) return null
  return (
    <section className="border-border/60 bg-muted/20 mb-6 rounded-lg border p-4" data-testid="document-schema-section">
      <DocumentSchemaPage document={document} />
    </section>
  )
}

/**
 * The schema view: the body of {@link DocumentSchemaSection}, and the `schema` route on its own. A
 * published schema (`schemaDefinition`) is browsed in place; a draft's working schema
 * (`schemaDraft`, e.g. from the Extend Schema flow) is EDITED in place — the full schema editor,
 * saved onto the draft like any metadata change, and frozen into an IPFS blob at publish.
 */
/**
 * The bundled library schema a document IS, by its path (`/timestamp` → timestamp), whatever
 * version of the blob it currently points at. Dependencies are computed over the bundled
 * library, so this is what names them.
 */
function bundledSlugForDocument(document: HMDocument): string | null {
  const name = (document.path || '').replace(/^\//, '')
  return name && HM_SCHEMAS[name] ? name : null
}

/** A published schema seeded into the editor sheds a legacy root `name`: the document carries it, and
 * the next publish writes a schema without it. The `description` stays — it is the schema's own. */
function stripLegacyLabels(schema: Record<string, any>): Record<string, any> {
  const {name: _n, ...rest} = schema
  return rest
}

function DocumentSchemaPage({document}: {document: HMDocument}) {
  const ctx = useDocumentSelector(selectContext)
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  const canEditCurrentRoute = useDocumentSelector(selectCanEditCurrentRoute)
  const navigate = useNavigate()
  const openUrl = useOpenUrl()
  // Draft metadata (partial) overrides published metadata, same as the Attributes tab.
  const metadata = {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
  const draftSchema = ctx.schemaDraft
  const cid = schemaDefinitionCid(metadata)
  const [schemaDialogOpen, setSchemaDialogOpen] = useState(false)
  // Write access to the document is write access to its schema: the published schema seeds the
  // editor, the first change starts a draft (`schemaDraft`), and publishing freezes that into a
  // new IPFS object the document then references. Bundled library schemas need no fetch.
  const bundled = cid ? nameForCid(cid) : undefined
  // The library schema this document is, by path — its blob may be an older version than the bundle.
  const librarySlug = bundledSlugForDocument(document)
  // A site that mirrors the library (the dev site, the Hypermedia site itself) links its own pages.
  const linkSpace = librarySlug ? document.account : undefined
  const navLibrary = (slug: string) => {
    const url = linkSpace ? libraryPageUrl(linkSpace, slug) : nameToUrl(slug)
    if (url) openUrl(url)
  }
  const {byCid, isLoading} = useSchemaRegistry(cid && !bundled && canEditCurrentRoute ? [cid] : [])
  const published = cid ? (bundled ? HM_SCHEMAS[bundled] : byCid[cid]) : undefined
  const edit = (next: Record<string, any>) => {
    beginEditIfNeeded()
    send({type: 'change', schemaDraft: next})
  }
  // Writers see the schema read-only too, with an Edit button that opens the editor in a dialog;
  // the view follows the draft as it is edited.
  if (canEditCurrentRoute && (draftSchema || published)) {
    const current = draftSchema ?? stripLegacyLabels(published!)
    const nav = navLibrary
    return (
      <div className="flex flex-col gap-2" data-testid={draftSchema ? 'schema-draft-view' : 'schema-view'}>
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            {draftSchema ? (
              // The draft is not a blob yet: render the object itself.
              <SchemaNavContext.Provider value={{openRef: (ref) => openUrl(ref)}}>
                <SchemaView schema={draftSchema} nav={nav} hideIdentity />
              </SchemaNavContext.Provider>
            ) : (
              // Exactly what readers see (core-type leads, variants, collapsed dependencies).
              <SchemaBrowserPage embedded cid={cid!} navigate={navigate} openUrl={openUrl} linkSpace={linkSpace} />
            )}
            {librarySlug && (draftSchema || !bundled) && <DepLists name={librarySlug} nav={nav} />}
          </div>
          <Button
            variant="ghost"
            size="sm"
            className="text-muted-foreground shrink-0 gap-1"
            data-testid="schema-edit-button"
            onClick={() => setSchemaDialogOpen(true)}
          >
            <Pencil className="size-3.5" /> Edit
          </Button>
        </div>
        <Dialog open={schemaDialogOpen} onOpenChange={setSchemaDialogOpen}>
          <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Edit schema</DialogTitle>
              <DialogDescription>
                Changes stay in the document draft; publishing the document publishes them as a new schema object.
              </DialogDescription>
            </DialogHeader>
            <SchemaEditor schema={current} onSchema={edit} />
          </DialogContent>
        </Dialog>
      </div>
    )
  }
  if (!cid) {
    return <div className="text-muted-foreground p-4 text-sm">This document does not define a schema.</div>
  }
  if (canEditCurrentRoute && isLoading) {
    return <div className="text-muted-foreground p-4 text-sm">Fetching schema…</div>
  }
  return (
    <>
      <SchemaBrowserPage embedded cid={cid} navigate={navigate} openUrl={openUrl} linkSpace={linkSpace} />
      {librarySlug && !bundled && <DepLists name={librarySlug} nav={navLibrary} />}
    </>
  )
}

/**
 * One of the two attributes-schema bindings, below the attribute rows on the Attributes tab — this
 * document's own (`attributesSchema`: the fields it carries) or its children's
 * (`childAttributesSchema`: the fields every document created inside it carries). Nothing bound:
 * a **Define** button; pressing it offers a search over schema pages (or a pasted `hm://` /
 * `ipfs://` reference) and a **Custom Attributes** / **Custom Children Attributes** button. Bound to a schema page: the page's pill and
 * its schema read-only (clear it to define a custom one). Bound to an object this document
 * owns (`ipfs://`, or a draft): the full schema editor in place — edits go to the draft's
 * `bindingSchemaDrafts`, and publishing freezes them into a new object the key then points at.
 * **Clear** removes the binding. Arriving with the route's `focus` on this key opens Define.
 */
function BindingSchemaSection({
  docId,
  document,
  bindingKey,
}: {
  docId: UnpackedHypermediaId
  document: HMDocument
  bindingKey: BindingSchemaKey
}) {
  const ctx = useDocumentSelector(selectContext)
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  const canEditCurrentRoute = useDocumentSelector(selectCanEditCurrentRoute)
  const route = useNavRoute()
  const openUrl = useOpenUrl()
  const metadata = {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
  const raw = (metadata as Record<string, unknown>)[bindingKey]
  const ref = typeof raw === 'string' && raw ? raw : null
  const draft = ctx.bindingSchemaDrafts?.[bindingKey] ?? null
  const cls = classifyRef(ref)
  const owned = cls.kind === 'cid'
  const {schema: published, isLoading} = useResolvedSchema(draft ? null : ref)
  // With no attributes schema of its own, a document's fields follow its parent's children
  // attributes schema — shown here, since it is what shapes the rows, with Define as the override.
  const effective = useEffectiveDocSchema(bindingKey === 'attributesSchema' ? docId : null, metadata)
  // The parent's children schema may still be a draft on this device; the rows already follow it.
  const parentDraft = useParentDraftChildAttributesSchema(
    bindingKey === 'attributesSchema' && !draft && !ref ? docId : null,
  )
  const inheritedSchema = parentDraft.schema ?? effective.schema
  const inheritedIsDraft = !!parentDraft.schema
  const inherited =
    bindingKey === 'attributesSchema' && !draft && !ref && (inheritedIsDraft || effective.source === 'inherited')
  const parentId = docId.path?.length ? hmId(docId.uid, {path: docId.path.slice(0, -1)}) : null
  const focused = route.key === 'metadata' && route.focus === bindingKey
  const label = bindingKey === 'attributesSchema' ? 'Attributes schema' : 'Children attributes schema'
  const blurb =
    bindingKey === 'attributesSchema'
      ? 'The fields this document carries.'
      : 'The fields every document created inside this one carries.'
  const edit = useCallback(
    (next: Record<string, any>) => {
      beginEditIfNeeded()
      send({type: 'change', bindingSchemaDrafts: {[bindingKey]: next}})
    },
    [beginEditIfNeeded, send, bindingKey],
  )
  // Point the binding at a reference (a schema page's hm:// URL, or an ipfs:// schema object),
  // dropping any draft. An empty reference clears the binding: a null tombstone removes the key.
  const setReference = useCallback(
    (value: unknown) => {
      const next = typeof value === 'string' ? value.trim() : ''
      beginEditIfNeeded()
      send({type: 'change', metadata: {[bindingKey]: next || null} as any, bindingSchemaDrafts: {[bindingKey]: null}})
    },
    [beginEditIfNeeded, send, bindingKey],
  )
  const clear = useCallback(() => setReference(''), [setReference])
  // Define: the search-or-custom choice, open until something is bound or it is cancelled.
  const [defining, setDefining] = useState(false)
  useEffect(() => {
    if (focused && canEditCurrentRoute && !draft && !ref) setDefining(true)
  }, [focused, canEditCurrentRoute, draft, ref])
  useEffect(() => {
    if (draft || ref) setDefining(false)
  }, [draft, ref])
  const sectionRef = useRef<HTMLElement>(null)
  useEffect(() => {
    if (focused) sectionRef.current?.scrollIntoView({block: 'start'})
  }, [focused])

  const header = (
    <div className="flex flex-col gap-0.5">
      <SizableText weight="semibold" size="sm">
        {label}
      </SizableText>
      <SizableText size="xs" className="text-muted-foreground">
        {blurb}
      </SizableText>
    </div>
  )
  const customStructButton = (seed?: Record<string, any>) => (
    <Button variant="outline" size="sm" onClick={() => edit(seed ? stripLegacyLabels(seed) : emptyStructSchema())}>
      <Braces className="mr-1 size-3.5" />{' '}
      {bindingKey === 'attributesSchema' ? 'Custom Attributes' : 'Custom Children Attributes'}
    </Button>
  )
  if (!draft && !ref) {
    if (!canEditCurrentRoute && !inherited) return null
    return (
      <section
        ref={sectionRef}
        className="border-border/60 mt-4 flex flex-col gap-3 rounded-lg border border-dashed p-3"
        data-testid={`binding-schema-${bindingKey}`}
      >
        <div className="flex items-center justify-between gap-3">
          {header}
          {!canEditCurrentRoute ? null : defining ? (
            <Button variant="ghost" size="sm" className="text-muted-foreground" onClick={() => setDefining(false)}>
              Cancel
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={() => setDefining(true)}>
              <Plus className="mr-1 size-4" /> {inherited ? 'Override' : 'Define'}
            </Button>
          )}
        </div>
        {defining ? (
          <div className="flex flex-wrap items-center gap-2">
            <div className="min-w-64 flex-1">
              <HMEntityField mode="schema" value="" onValue={setReference} onOpen={openUrl} autoFocus />
            </div>
            <SizableText size="xs" className="text-muted-foreground">
              or
            </SizableText>
            {customStructButton()}
          </div>
        ) : null}
        {inherited && !defining ? (
          <div className="flex flex-col gap-2" data-testid="binding-schema-inherited">
            <div className="flex flex-wrap items-center gap-2">
              <SizableText size="xs" className="text-muted-foreground">
                {inheritedIsDraft
                  ? "Inherited from the parent's children attributes schema (an unpublished draft)"
                  : "Inherited from the parent's children attributes schema"}
              </SizableText>
              {parentId ? <HMEntityLink url={parentId.id} mode="document" onOpen={openUrl} /> : null}
            </div>
            {inheritedSchema ? (
              <SchemaNavContext.Provider value={{openRef: (r) => openUrl(r)}}>
                <SchemaView
                  schema={inheritedSchema}
                  nav={(slug) => {
                    const url = nameToUrl(slug)
                    if (url) openUrl(url)
                  }}
                  hideIdentity
                />
              </SchemaNavContext.Provider>
            ) : (
              <SizableText size="sm" className="text-muted-foreground">
                {effective.isLoading || parentDraft.isLoading
                  ? 'Fetching schema…'
                  : 'Could not resolve the parent’s schema.'}
              </SizableText>
            )}
          </div>
        ) : null}
      </section>
    )
  }
  const editable = canEditCurrentRoute && (!!draft || owned)
  const current = draft ?? published
  return (
    <section
      ref={sectionRef}
      className="border-border/60 bg-muted/20 mt-4 flex flex-col gap-3 rounded-lg border p-3"
      data-testid={`binding-schema-${bindingKey}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        {header}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {draft ? (
            <SizableText size="xs" className="text-muted-foreground">
              Publishing the document publishes this schema.
            </SizableText>
          ) : ref && !owned ? (
            // A schema page: its pill (✕ clears). An object this document owns has no page to name;
            // the schema itself is shown below, and Clear removes it.
            <HMEntityField
              mode="schema"
              value={ref}
              onValue={setReference}
              onOpen={openUrl}
              onClear={canEditCurrentRoute ? clear : undefined}
            />
          ) : null}
          {ref && owned && !draft && canEditCurrentRoute ? (
            <Button variant="ghost" size="sm" className="text-muted-foreground gap-1" onClick={clear}>
              <Trash className="size-3.5" /> Clear
            </Button>
          ) : null}
          {canEditCurrentRoute && draft ? (
            <Button variant="ghost" size="sm" className="text-muted-foreground gap-1" onClick={clear}>
              <Trash className="size-3.5" /> Clear
            </Button>
          ) : null}
        </div>
      </div>
      {editable && current ? (
        <SchemaEditor schema={current} onSchema={edit} />
      ) : current ? (
        <SchemaNavContext.Provider value={{openRef: (r) => openUrl(r)}}>
          <SchemaView
            schema={current}
            nav={(slug) => {
              const url = nameToUrl(slug)
              if (url) openUrl(url)
            }}
            hideIdentity
          />
        </SchemaNavContext.Provider>
      ) : (
        <SizableText size="sm" className="text-muted-foreground">
          {isLoading ? 'Fetching schema…' : `Could not resolve ${ref}.`}
        </SizableText>
      )}
    </section>
  )
}

/**
 * The schema this document DEFINES (`schemaDefinition`), at the bottom of the Attributes tab beside
 * the attributes-schema bindings, with the full schema editor in place. Nothing defined: a Define
 * button that starts an empty struct. Defined or drafting: the editor — edits go to the draft's
 * working schema (`schemaDraft`), and publishing freezes it into a new IPFS object the key then
 * points at. Clear removes the definition. Readers see the schema read-only.
 */
function SchemaDefinitionSection({document}: {document: HMDocument}) {
  const ctx = useDocumentSelector(selectContext)
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  const canEditCurrentRoute = useDocumentSelector(selectCanEditCurrentRoute)
  const openUrl = useOpenUrl()
  const metadata = {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
  const cid = schemaDefinitionCid(metadata)
  const draft = ctx.schemaDraft ?? null
  const {schema: published, isLoading} = useResolvedSchema(draft || !cid ? null : `ipfs://${cid}`)
  const edit = useCallback(
    (next: Record<string, any>) => {
      beginEditIfNeeded()
      send({type: 'change', schemaDraft: next})
    },
    [beginEditIfNeeded, send],
  )
  const clear = useCallback(() => {
    beginEditIfNeeded()
    send({type: 'change', metadata: {schemaDefinition: null} as any, schemaDraft: null})
  }, [beginEditIfNeeded, send])

  const header = (
    <div className="flex flex-col gap-0.5">
      <SizableText weight="semibold" size="sm">
        Schema definition
      </SizableText>
      <SizableText size="xs" className="text-muted-foreground">
        The schema this document defines, for other documents to use.
      </SizableText>
    </div>
  )
  if (!draft && !cid) {
    if (!canEditCurrentRoute) return null
    return (
      <section
        className="border-border/60 mt-4 flex items-center justify-between gap-3 rounded-lg border border-dashed p-3"
        data-testid="schema-definition-section"
      >
        {header}
        <Button variant="outline" size="sm" onClick={() => edit(emptyStructSchema())}>
          <Plus className="mr-1 size-4" /> Define
        </Button>
      </section>
    )
  }
  const current = draft ?? (published ? stripLegacyLabels(published) : undefined)
  return (
    <section
      className="border-border/60 bg-muted/20 mt-4 flex flex-col gap-3 rounded-lg border p-3"
      data-testid="schema-definition-section"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        {header}
        <div className="flex shrink-0 flex-wrap items-center gap-2">
          {draft ? (
            <SizableText size="xs" className="text-muted-foreground">
              Publishing the document publishes this schema.
            </SizableText>
          ) : null}
          {canEditCurrentRoute ? (
            <Button variant="ghost" size="sm" className="text-muted-foreground gap-1" onClick={clear}>
              <Trash className="size-3.5" /> Clear
            </Button>
          ) : null}
        </div>
      </div>
      {current && canEditCurrentRoute ? (
        <SchemaEditor schema={current} onSchema={edit} />
      ) : current ? (
        <SchemaNavContext.Provider value={{openRef: (r) => openUrl(r)}}>
          <SchemaView
            schema={current}
            nav={(slug) => {
              const url = nameToUrl(slug)
              if (url) openUrl(url)
            }}
            hideIdentity
          />
        </SchemaNavContext.Provider>
      ) : (
        <SizableText size="sm" className="text-muted-foreground">
          {isLoading ? 'Fetching schema…' : `Could not resolve ipfs://${cid}.`}
        </SizableText>
      )}
    </section>
  )
}

function DocumentMetadataPage({
  docId,
  document,
  fileUpload,
}: {
  docId: UnpackedHypermediaId
  document: HMDocument
  fileUpload?: (file: File) => Promise<string>
}) {
  const route = useNavRoute()
  const replaceRoute = useNavigate('replace')
  const ctx = useDocumentSelector(selectContext)
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  const canEditCurrentRoute = useDocumentSelector(selectCanEditCurrentRoute)
  const openUrl = useOpenUrl()

  // Draft metadata (partial) overrides published metadata, same as the options panel.
  const metadata = {...(ctx.document?.metadata || document.metadata || {}), ...ctx.metadata}
  // The schema this document conforms to (own `attributesSchema`, else parent's
  // `childAttributesSchema`) — drives required-field rows + advisory validation. While the
  // document's own attributes schema is being drafted below, the draft is the schema: every edit
  // to it reshapes the rows and their validation at once, before anything is published.
  // …and with no schema of its own, the parent's DRAFTED children schema counts too (desktop).
  const {schema: conformanceSchema, registry: conformanceRegistry} = useConformanceSchema(
    docId,
    metadata,
    ctx.bindingSchemaDrafts?.attributesSchema,
  )

  // Open an uploaded IPFS file reference in its own dedicated viewer window/tab.
  const openFile = useCallback((cid: string) => openUrl(`hm://inspect/ipfs/${cid}`, true), [openUrl])
  // Open a blank blob editor (new IPFS object) in its own window; the `new`
  // sentinel path puts the viewer into create mode.
  const onCreateBlob = useCallback(() => openUrl('hm://inspect/ipfs/new', true), [openUrl])

  // Direct, in-context editing of the IPFS objects a PUBLISHED document's
  // metadata references (schemaDefinition, …): the blob page opens in the
  // field's context and publishing updates the document directly. Not offered
  // for a field the draft has already changed — the draft would win on publish.
  const navigate = useNavigate()
  const isPublished = !!(ctx.document?.version || document.version)
  const draftPatch = ctx.metadata || {}
  const directEdit = useMemo(
    () =>
      isPublished && canEditCurrentRoute
        ? {
            isFieldEditable: (key: string) => (draftPatch as Record<string, unknown>)[key] === undefined,
            onEditField: (key: string, cid: string) =>
              navigate({key: 'inspect-ipfs', ipfsPath: cid, editField: {docUrl: docId.id, field: key}}),
          }
        : undefined,
    [isPublished, canEditCurrentRoute, draftPatch, navigate, docId.id],
  )

  return (
    <>
      <DocumentMetadataView
        metadata={metadata as any}
        canEdit={canEditCurrentRoute}
        conformanceSchema={conformanceSchema}
        conformanceRegistry={conformanceRegistry}
        onMetadata={(patch) => {
          if (!canEditCurrentRoute) return
          beginEditIfNeeded()
          send({type: 'change', metadata: patch as any})
        }}
        fileUpload={fileUpload}
        openFile={openFile}
        openUrl={openUrl}
        onCreateBlob={onCreateBlob}
        directEdit={directEdit}
        addRawFieldOpen={route.key === 'metadata' && !!route.addRawField}
        onAddRawFieldOpenChange={(open) => {
          if (!open && route.key === 'metadata' && route.addRawField) {
            const {addRawField: _a, ...rest} = route
            replaceRoute(rest)
          }
        }}
      />
      {BINDING_SCHEMA_KEYS.map((bindingKey) => (
        <BindingSchemaSection key={bindingKey} docId={docId} document={document} bindingKey={bindingKey} />
      ))}
      <SchemaDefinitionSection document={document} />
    </>
  )
}

/**
 * Selects the main document view while retaining collection search and filter state
 * across view switches. Hidden collections stay out of keyboard and screen-reader navigation.
 */
export function DocumentViewContent({
  activeView,
  collection,
  children,
}: {
  activeView: ActiveView
  collection?: ReactNode
  children: ReactNode
}) {
  return (
    <>
      {collection ? <div hidden={activeView !== 'content'}>{collection}</div> : null}
      {!collection || activeView !== 'content' ? children : null}
    </>
  )
}

/** Renders a collection with saved settings and temporary reader controls. */
export function DocumentCollection({
  docId,
  queryBlock,
  canEdit,
}: {
  docId: UnpackedHypermediaId
  queryBlock: EditorBlock | null
  canEdit: boolean
}) {
  const {DraftSlot} = useQueryBlockDrafts()
  const client = useUniversalClient()
  const send = useDocumentSend()
  const {beginEditIfNeeded} = useEditorGate()
  const props = queryBlock?.type === 'query' ? queryBlock.props : undefined
  const includes = useMemo<HMBlockQuery['attributes']['query']['includes']>(() => {
    try {
      const parsed = JSON.parse(props?.queryIncludes || '[]')
      if (!Array.isArray(parsed) || !parsed.length) return []
      return parsed.map((include) =>
        include.space ? include : {...include, space: docId.uid, path: (docId.path ?? []).join('/')},
      )
    } catch {
      return []
    }
  }, [docId.path, docId.uid, props?.queryIncludes])
  const savedQuerySort = useMemo(() => {
    try {
      return normalizeQuerySort(JSON.parse(props?.querySort || '[]'))
    } catch {
      return []
    }
  }, [props?.querySort])
  const [viewerSort, setViewerSort] = useState<{
    documentId: string
    sort: typeof savedQuerySort | null
  }>({documentId: docId.id, sort: null})
  if (viewerSort.documentId !== docId.id) {
    setViewerSort({documentId: docId.id, sort: null})
  }
  const querySort = !canEdit && viewerSort.documentId === docId.id ? viewerSort.sort ?? savedQuerySort : savedQuerySort
  const serverQuerySort =
    !canEdit && querySort.some(({term, reverse}) => !sortTermToDocumentSort(term, reverse)) ? savedQuerySort : querySort
  const queryLimit = Number.parseInt(props?.queryLimit || '', 10) || undefined
  const style = (props?.style as 'Card' | 'List' | 'Table') || 'Table'
  const columnCount = props?.columnCount || '3'
  const banner = props?.banner === 'true'
  const [viewerSearch, setViewerSearch] = useState('')
  const [viewerFilters, setViewerFilters] = useState<NonNullable<NonNullable<HMQueryBlockInput['viewer']>['filters']>>(
    [],
  )
  const debouncedViewerSearch = useDebounce(viewerSearch.trim(), 250)
  const queryInput = useMemo<HMQueryBlockInput | null>(() => {
    if (!includes.length) return null
    const filters = viewerFilters.filter((filter) => filter.value.trim())
    return {
      query: {includes, sort: serverQuerySort, limit: queryLimit},
      ...((debouncedViewerSearch || filters.length) && {
        viewer: {
          ...(debouncedViewerSearch ? {search: debouncedViewerSearch} : {}),
          ...(filters.length ? {filters} : {}),
        },
      }),
    }
  }, [debouncedViewerSearch, includes, queryLimit, serverQuerySort, viewerFilters])
  const query = useQuery(queryQueryBlock(client, queryInput))
  const tableConfig = useMemo<HMQueryTableConfig | undefined>(() => {
    try {
      return props?.tableConfig ? JSON.parse(props.tableConfig) : undefined
    } catch {
      return undefined
    }
  }, [props?.tableConfig])

  const updateQueryProps = useCallback(
    (changes: Partial<EditorQueryBlock['props']>) => {
      if (!canEdit) return
      beginEditIfNeeded()
      send({type: 'collection.query.change', props: changes})
    },
    [canEdit, beginEditIfNeeded, send],
  )

  const handleTableConfigChange = useCallback(
    (config: HMQueryTableConfig) => {
      if (!canEdit) return
      beginEditIfNeeded()
      send({type: 'collection.query.change', props: {tableConfig: JSON.stringify(config)}})
    },
    [canEdit, beginEditIfNeeded, send],
  )

  const handleTableSortingChange = useCallback(
    (sorting: import('@tanstack/react-table').SortingState) => {
      const sort = sorting.map(({id, desc}) => ({term: id, reverse: desc}))
      if (!canEdit) {
        setViewerSort({documentId: docId.id, sort})
        return
      }
      beginEditIfNeeded()
      send({
        type: 'collection.query.change',
        props: {
          querySort: JSON.stringify(sort),
        },
      })
    },
    [canEdit, docId.id, beginEditIfNeeded, send],
  )

  const renderContent = (slot: QueryBlockDraftSlotData | null) => {
    const {prependItems, bannerContent, tableDrafts} = buildSlotItems(slot, style, banner, !!query.data?.results.length)
    return (
      <QueryBlockContent
        toolbarActions={
          <div className="flex items-center gap-2">
            <Tabs
              value={style}
              onValueChange={(value) => updateQueryProps({style: value as 'Card' | 'List' | 'Table'})}
            >
              <TabsList>
                <TabsTrigger value="Table">
                  <TableIcon />
                  Table
                </TabsTrigger>
                <TabsTrigger value="List">
                  <ListIcon />
                  List
                </TabsTrigger>
                <TabsTrigger value="Card">
                  <LayoutGrid />
                  Cards
                </TabsTrigger>
              </TabsList>
            </Tabs>
            <Popover>
              <Tooltip content="Query settings">
                <PopoverTrigger asChild>
                  <Button variant="ghost" size="icon" aria-label="Query settings">
                    <MoreHorizontal className="size-4" />
                  </Button>
                </PopoverTrigger>
              </Tooltip>
              <PopoverContent align="end" className="w-80">
                <CollectionQuerySettings props={props} onChange={updateQueryProps} />
              </PopoverContent>
            </Popover>
          </div>
        }
        items={query.data?.results ?? []}
        style={style}
        columnCount={columnCount}
        banner={bannerContent ? false : banner}
        bannerContent={bannerContent}
        prependItems={prependItems}
        tableDrafts={tableDrafts}
        onCreateDocument={slot?.onCreateDraft}
        accountsMetadata={query.data?.accountsMetadata ?? {}}
        interactionSummaries={query.data?.interactionSummaries ?? {}}
        isDiscovering={query.isLoading}
        tableConfig={tableConfig}
        onTableConfigChange={handleTableConfigChange}
        tableSorting={querySort.map(({term, reverse}) => ({id: term, desc: reverse}))}
        onTableSortingChange={handleTableSortingChange}
        navigateCards
        viewerSearch={viewerSearch}
        onViewerSearchChange={setViewerSearch}
        viewerFilters={viewerFilters}
        onViewerFiltersChange={setViewerFilters}
        totalMatches={query.data?.totalMatches}
        filterOptions={query.data?.filterOptions}
        isUpdating={query.isFetching && !query.isLoading}
        viewerQueryApplied={query.data?.totalMatches !== undefined}
      />
    )
  }

  return (
    <div className="w-full px-5 pt-4 pb-8">
      <div className="border-border bg-background overflow-hidden rounded-xl border">
        {DraftSlot ? <DraftSlot targetId={docId}>{renderContent}</DraftSlot> : renderContent(null)}
      </div>
    </div>
  )
}

function CollectionQuerySettings({
  props,
  onChange,
}: {
  props?: Partial<EditorQueryBlock['props']>
  onChange: (changes: Partial<EditorQueryBlock['props']>) => void
}) {
  const limit = props?.queryLimit || ''
  const columnCount = props?.columnCount || '3'
  const banner = props?.banner === 'true'

  return (
    <div className="flex flex-col gap-3 p-1">
      <SelectField
        id="query-column-count"
        label="Columns"
        options={[
          {value: '1', label: '1 column'},
          {value: '2', label: '2 columns'},
          {value: '3', label: '3 columns'},
        ]}
        value={columnCount}
        onValue={(value) => onChange({columnCount: value as '1' | '2' | '3'})}
      />
      <TextField
        id="query-limit"
        label="Limit"
        type="number"
        value={limit}
        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
          onChange({queryLimit: e.target.value ? String(Number.parseInt(e.target.value, 10)) : ''})
        }
      />
      <SwitchField
        id="query-banner"
        label="Show banner"
        checked={banner}
        onCheckedChange={(checked: boolean) => onChange({banner: checked ? 'true' : 'false'})}
      />
    </div>
  )
}

function MainContent({
  docId,
  resourceId,
  document,
  activeView,
  contentMaxWidth,
  wrapperProps,
  sidebarProps,
  mainContentProps,
  showSidebars,
  showCollapsed,
  discussionsParams,
  suppressCommentEditor,
  activityFilterEventType,
  onActivityFilterChange,
  blockCitations,
  citationFragmentHighlights,
  onCitationFragmentClick,
  onBlockCitationClick,
  onBlockCommentClick,
  onBlockSelect,
  onTextSelection,
  isUnpublishedDraft,
  isBlockInPublishedVersion,
  CommentEditor,
  siteUrl,
  inlineCards,
  inlineInsert,
  DocumentContentComponent,
  onEditorReady,
  existingDraftContent,
  existingDraftCursorPosition,
  ssrContentHTML,
  perspectiveAccountUid,
  linkExtensionOptions,
  fileUpload,
  draftVersionEntry,
}: {
  docId: UnpackedHypermediaId
  resourceId: UnpackedHypermediaId
  document: HMDocument
  activeView: ActiveView
  contentMaxWidth: number
  wrapperProps: React.HTMLAttributes<HTMLDivElement>
  sidebarProps: React.HTMLAttributes<HTMLDivElement>
  mainContentProps: React.HTMLAttributes<HTMLDivElement>
  showSidebars: boolean
  showCollapsed: boolean
  discussionsParams?: {
    openComment?: string
    replyCommentId?: string
    targetBlockId?: string
    blockId?: string
    blockRange?: import('@seed-hypermedia/client/hm-types').BlockRange | null
    autoFocus?: boolean
    isReplying?: boolean
    replyCommentVersion?: string
    rootReplyCommentVersion?: string
  }
  suppressCommentEditor?: boolean
  activityFilterEventType?: string[]
  onActivityFilterChange?: (filter: {filterEventType?: string[]}) => void
  blockCitations?: Record<string, {citations: number; comments: number}> | null
  citationFragmentHighlights?: CitationFragmentHighlight[]
  onCitationFragmentClick?: (event: CitationFragmentClick) => void
  onBlockCitationClick?: (blockId?: string | null) => void
  onBlockCommentClick?: (
    blockId?: string | null,
    blockRange?: BlockRange | undefined,
    startCommentingNow?: boolean,
  ) => void
  onBlockSelect?: (blockId: string, opts?: BlockRangeSelectOptions) => void
  onTextSelection?: () => void
  isUnpublishedDraft?: boolean
  isBlockInPublishedVersion?: (blockId: string) => boolean
  CommentEditor?: React.ComponentType<CommentEditorProps>
  directory?: import('@seed-hypermedia/client/hm-types').HMDocumentInfo[]
  siteUrl?: string
  inlineCards?: ReactNode
  inlineInsert?: ReactNode
  DocumentContentComponent?: React.ComponentType<DocumentContentProps>
  onEditorReady?: (editor: any) => void
  existingDraftContent?: HMBlockNode[]
  existingDraftCursorPosition?: number
  ssrContentHTML?: string | null
  perspectiveAccountUid?: string | null
  linkExtensionOptions?: LinkExtensionOptions
  fileUpload?: (file: File) => Promise<string>
  draftVersionEntry?: DraftVersionEntry
}) {
  const {openRouteNewWindow, originHomeId} = useUniversalAppContext()
  const navigate = useNavigate()
  const replaceRoute = useNavigate('replace')
  const route = useNavRoute()
  const allDocumentsSiteId = !IS_DESKTOP && originHomeId ? hmId(originHomeId.uid) : hmId(docId.uid)
  const rawExploreQuery = route.key === 'explore' ? route.q || '' : ''
  const legacyExploreSort =
    route.key === 'explore' && route.sort && !/\bsort:/.test(rawExploreQuery) ? `sort:${route.sort}` : ''
  const exploreQuery = [rawExploreQuery, legacyExploreSort].filter(Boolean).join(' ')
  const parsedExploreQuery = useMemo(() => parseExploreQuery(exploreQuery), [exploreQuery])
  // The open result tab narrows the request, so a page of results is all of the kind being read.
  const [activeExploreType, setActiveExploreType] = useState<HMExploreResultType | null>(null)
  const explore = useExploreResults(
    parsedExploreQuery,
    {type: 'site', id: allDocumentsSiteId},
    {
      enabled: activeView === 'explore',
      typeFilter: activeExploreType,
    },
  )

  switch (activeView) {
    case 'all-documents':
      return (
        <AllDocumentsPage
          siteId={allDocumentsSiteId}
          scopeId={allDocumentsSiteId}
          onNavigateToDocument={(id, opts) => {
            const route = {key: 'document' as const, id}
            if (opts?.newWindow) {
              if (openRouteNewWindow) {
                openRouteNewWindow(route)
              } else {
                const href = routeToHref(route, {originHomeId})
                if (href) window.open(href, '_blank')
              }
              return
            }
            navigate(route)
          }}
        />
      )

    case 'explore':
      return (
        <ExplorePage
          contextLabel={`Space: ${allDocumentsSiteId.uid}`}
          query={exploreQuery}
          parsed={parsedExploreQuery}
          results={explore.results}
          counts={explore.counts}
          textTerms={explore.textTerms}
          diagnostics={explore.diagnostics}
          blocksByDocument={explore.blocksByDocument}
          hasMore={explore.hasMore}
          intersectionPending={explore.intersectionPending}
          intersectionTruncated={explore.intersectionTruncated}
          onLoadMore={explore.loadMore}
          isLoading={explore.isLoading}
          error={explore.error instanceof Error ? explore.error.message : null}
          onQueryChange={(q) => replaceRoute({key: 'explore', context: {type: 'site', id: allDocumentsSiteId}, q})}
          accountUid={allDocumentsSiteId.uid}
          context={{type: 'site', id: allDocumentsSiteId}}
          onOpenResult={(result) => {
            if (result.type === 'comment') {
              navigate({key: 'comments', id: result.documentId, openComment: result.commentId})
              return
            }
            if (result.type === 'contact') {
              navigate({key: 'profile', id: result.id})
              return
            }
            navigate({key: 'document', id: result.id})
          }}
          onActiveTypeChange={setActiveExploreType}
        />
      )

    case 'directory':
      return <DirectoryPageContent docId={docId} showTitle contentMaxWidth={contentMaxWidth} />

    case 'collaborators':
      return (
        <PageLayout contentMaxWidth={contentMaxWidth} contentClassName="px-4">
          <CollaboratorsPage docId={docId} />
        </PageLayout>
      )

    case 'metadata':
      return (
        <PageLayout contentMaxWidth={contentMaxWidth} contentClassName="px-4">
          <DocumentMetadataPage docId={docId} document={document} fileUpload={fileUpload} />
        </PageLayout>
      )

    case 'schema':
      return (
        <PageLayout contentMaxWidth={contentMaxWidth}>
          <div className="pl-4">
            <DocumentSchemaPage document={document} />
          </div>
        </PageLayout>
      )

    case 'activity':
      if (activityFilterToSlug(activityFilterEventType) === 'versions') {
        return (
          <PageLayout contentMaxWidth={contentMaxWidth}>
            <DocumentVersionsPanel
              size="md"
              docId={docId}
              targetDomain={siteUrl}
              draftVersionEntry={draftVersionEntry}
            />
          </PageLayout>
        )
      }
      if (activityFilterToSlug(activityFilterEventType) === 'citations') {
        return (
          <PageLayout contentMaxWidth={contentMaxWidth}>
            <ConnectionsPanel docId={docId} targetBlockId={docId.blockRef || undefined} />
          </PageLayout>
        )
      }
      return (
        <PageLayout contentMaxWidth={contentMaxWidth}>
          <FeedFilters filterEventType={activityFilterEventType} onFilterChange={onActivityFilterChange} />
          <Feed
            size="md"
            filterResource={docId.id}
            filterEventType={activityFilterEventType || []}
            targetDomain={siteUrl}
            draftVersionEntry={draftVersionEntry}
          />
        </PageLayout>
      )

    case 'comments':
      return (
        <DiscussionsPageContent
          docId={docId}
          showTitle={false}
          contentMaxWidth={contentMaxWidth}
          targetDomain={siteUrl}
          openComment={discussionsParams?.openComment}
          replyCommentId={discussionsParams?.replyCommentId}
          targetBlockId={discussionsParams?.targetBlockId}
          blockId={discussionsParams?.blockId}
          blockRange={discussionsParams?.blockRange}
          commentEditor={
            CommentEditor && !suppressCommentEditor ? (
              <CommentEditor
                key={getCommentEditorRouteKey({
                  openComment: discussionsParams?.openComment,
                  targetBlockId: discussionsParams?.targetBlockId,
                  blockRange: discussionsParams?.blockRange,
                })}
                docId={docId}
                quotingBlockId={discussionsParams?.targetBlockId}
                quotingRange={extractQuotingRange(discussionsParams?.blockRange)}
                commentId={discussionsParams?.openComment}
                isReplying={discussionsParams?.isReplying ?? !!discussionsParams?.openComment}
                replyCommentVersion={discussionsParams?.replyCommentVersion}
                rootReplyCommentVersion={discussionsParams?.rootReplyCommentVersion}
                focusOnMount={discussionsParams?.autoFocus}
              />
            ) : undefined
          }
        />
      )

    case 'content':
    default:
      return (
        <ContentViewWithOutline
          docId={docId}
          resourceId={resourceId}
          document={document}
          wrapperProps={wrapperProps}
          sidebarProps={sidebarProps}
          mainContentProps={mainContentProps}
          showSidebars={showSidebars}
          showCollapsed={showCollapsed}
          blockCitations={blockCitations}
          citationFragmentHighlights={citationFragmentHighlights}
          onCitationFragmentClick={onCitationFragmentClick}
          onBlockCitationClick={onBlockCitationClick}
          onBlockCommentClick={onBlockCommentClick}
          onBlockSelect={onBlockSelect}
          onTextSelection={onTextSelection}
          isUnpublishedDraft={isUnpublishedDraft}
          isBlockInPublishedVersion={isBlockInPublishedVersion}
          inlineCards={inlineCards}
          inlineInsert={inlineInsert}
          DocumentContentComponent={DocumentContentComponent}
          onEditorReady={onEditorReady}
          existingDraftContent={existingDraftContent}
          existingDraftCursorPosition={existingDraftCursorPosition}
          ssrContentHTML={ssrContentHTML}
          perspectiveAccountUid={perspectiveAccountUid}
          linkExtensionOptions={linkExtensionOptions}
          fileUpload={fileUpload}
        />
      )
  }
}

function ContentViewWithOutline({
  docId,
  resourceId,
  document,
  wrapperProps,
  sidebarProps,
  mainContentProps,
  showSidebars,
  showCollapsed,
  blockCitations,
  citationFragmentHighlights,
  onCitationFragmentClick,
  onBlockCitationClick,
  onBlockCommentClick,
  onBlockSelect,
  onTextSelection,
  isUnpublishedDraft,
  isBlockInPublishedVersion,
  inlineCards,
  inlineInsert,
  DocumentContentComponent,
  onEditorReady,
  existingDraftContent,
  existingDraftCursorPosition,
  ssrContentHTML,
  perspectiveAccountUid,
  linkExtensionOptions,
  fileUpload,
}: {
  docId: UnpackedHypermediaId
  resourceId: UnpackedHypermediaId
  document: HMDocument
  wrapperProps: React.HTMLAttributes<HTMLDivElement>
  sidebarProps: React.HTMLAttributes<HTMLDivElement>
  mainContentProps: React.HTMLAttributes<HTMLDivElement>
  showSidebars: boolean
  showCollapsed: boolean
  blockCitations?: Record<string, {citations: number; comments: number}> | null
  citationFragmentHighlights?: CitationFragmentHighlight[]
  onCitationFragmentClick?: (event: CitationFragmentClick) => void
  onBlockCitationClick?: (blockId?: string | null) => void
  onBlockCommentClick?: (
    blockId?: string | null,
    blockRange?: BlockRange | undefined,
    startCommentingNow?: boolean,
  ) => void
  onBlockSelect?: (blockId: string, opts?: BlockRangeSelectOptions) => void
  onTextSelection?: () => void
  isUnpublishedDraft?: boolean
  isBlockInPublishedVersion?: (blockId: string) => boolean
  inlineCards?: ReactNode
  inlineInsert?: ReactNode
  DocumentContentComponent?: React.ComponentType<DocumentContentProps>
  onEditorReady?: (editor: any) => void
  existingDraftContent?: HMBlockNode[]
  existingDraftCursorPosition?: number
  ssrContentHTML?: string | null
  perspectiveAccountUid?: string | null
  linkExtensionOptions?: LinkExtensionOptions
  fileUpload?: (file: File) => Promise<string>
}) {
  const ctx = useDocumentSelector(selectContext)
  const rootChildrenType = (ctx.metadata?.childrenType ?? document.metadata?.childrenType) || 'Group'
  // Required custom attributes (from the doc's schema) render above the body.
  const canEdit = useDocumentSelector(selectCanEdit)
  const isEditing = useDocumentSelector(selectIsEditing)
  const send = useDocumentSend()
  const openUrl = useOpenUrl()
  const requiredAttrMetadata = useMemo(
    () => ({...document.metadata, ...ctx.metadata}),
    [document.metadata, ctx.metadata],
  )
  // The schema this document must conform to (own `attributesSchema`, else parent's
  // `childAttributesSchema`) — drives the always-visible required attributes. A drafted own
  // attributes schema (Attributes tab) takes precedence, so the rows follow it live.
  const {schema: conformanceSchema, registry: conformanceRegistry} = useConformanceSchema(
    resourceId,
    requiredAttrMetadata,
    ctx.bindingSchemaDrafts?.attributesSchema,
  )
  // existingDraftContent may arrive in HMBlockNode[] or
  // EditorBlock[] shape. Pick the outline builder that matches.
  const outlineSource = existingDraftContent ?? document.content ?? []
  const firstOutlineBlock = outlineSource[0] as any
  const isEditorFormat = firstOutlineBlock != null && 'type' in firstOutlineBlock && !('block' in firstOutlineBlock)
  const outlineMentionNames = useMentionNames(
    useMemo(
      () => (isEditorFormat ? getDraftMentionRefs(outlineSource as any) : extractRefs(outlineSource)),
      [outlineSource, isEditorFormat],
    ),
  )
  const outline = useMemo(
    () =>
      isEditorFormat
        ? getDraftNodesOutline(outlineSource as any, docId, undefined, outlineMentionNames)
        : getNodesOutline(outlineSource, docId, undefined, outlineMentionNames),
    [outlineSource, isEditorFormat, docId, outlineMentionNames],
  )
  const handleFileAttachment = useMemo<DocumentContentProps['handleFileAttachment'] | undefined>(() => {
    if (!fileUpload) return undefined
    return async (file: File) => {
      const attachment = await processImageIfSupported(file, CONTENT_IMAGE_POLICY)
      const cid = await fileUpload(attachment)
      return {
        url: cid.startsWith('ipfs://') ? cid : `ipfs://${cid}`,
        displaySrc: '',
        mime: attachment.type,
        name: attachment.name,
      }
    }
  }, [fileUpload])

  return (
    <div {...wrapperProps} className={cn(wrapperProps.className, 'flex')}>
      {showSidebars && (
        <div {...sidebarProps}>
          {outline.length > 0 && (
            <div className="sticky top-4 mt-4">
              <DocNavigationWrapper showCollapsed={showCollapsed} outline={outline}>
                <DocumentOutline
                  onActivateBlock={(blockId) => {
                    const el = window.document.getElementById(blockId)
                    if (el) {
                      el.scrollIntoView({behavior: 'smooth', block: 'start'})
                    }
                  }}
                  outline={outline}
                  id={docId}
                  activeBlockId={resourceId.blockRef}
                />
              </DocNavigationWrapper>
            </div>
          )}
        </div>
      )}

      <div {...mainContentProps} className={cn(mainContentProps.className, 'px-4 pt-8')}>
        <DocumentSchemaSection document={document} canEdit={canEdit} />
        {/* Everyone sees the required attributes; writers edit them in place. */}
        <RequiredAttributesEditor
          conformanceSchema={conformanceSchema}
          conformanceRegistry={conformanceRegistry}
          metadata={requiredAttrMetadata}
          openUrl={openUrl}
          onMetadata={
            canEdit
              ? (patch) => {
                  // A published doc isn't editing yet — enter editing first so the
                  // `change` is accepted (drafts are already in the editing state).
                  if (!isEditing) send({type: 'edit.start'})
                  send({type: 'change', metadata: patch})
                }
              : undefined
          }
        />
        <DocumentContentHandoff ssrContentHTML={ssrContentHTML} editorMounted={!!DocumentContentComponent}>
          {DocumentContentComponent ? (
            <DocumentContentComponent
              blocks={existingDraftContent ?? document.content}
              resourceId={resourceId}
              rootChildrenType={rootChildrenType}
              focusBlockId={resourceId.blockRef ?? undefined}
              focusBlockRange={resourceId.blockRange ?? undefined}
              citationFragmentHighlights={citationFragmentHighlights}
              onCitationFragmentClick={onCitationFragmentClick}
              blockCitations={blockCitations}
              onBlockCitationClick={onBlockCitationClick}
              onBlockCommentClick={onBlockCommentClick}
              onBlockSelect={onBlockSelect}
              onTextSelection={onTextSelection}
              onEditorReady={onEditorReady}
              draftCursorPosition={existingDraftCursorPosition}
              perspectiveAccountUid={perspectiveAccountUid}
              linkExtensionOptions={linkExtensionOptions}
              isUnpublishedDraft={isUnpublishedDraft}
              isBlockInPublishedVersion={isBlockInPublishedVersion}
              handleFileAttachment={handleFileAttachment}
            />
          ) : null}
        </DocumentContentHandoff>
        {inlineInsert}
        {inlineCards}
      </div>

      {showSidebars && <div {...sidebarProps} />}
    </div>
  )
}

/**
 * Swaps the server-rendered document HTML for the live editor without a
 * blank paint. The editor's React node views flush a frame after the
 * ProseMirror DOM mounts, so unmounting the placeholder in the same commit
 * paints one frame of empty block containers (content flashes and the page
 * height collapses). Instead the placeholder stays stacked above the
 * mounting editor — and the container holds its height — for two animation
 * frames, by which point the node views have rendered underneath.
 */
function DocumentContentHandoff({
  ssrContentHTML,
  editorMounted,
  children,
}: {
  ssrContentHTML?: string | null
  editorMounted: boolean
  children: ReactNode
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const placeholderRef = useRef<HTMLDivElement>(null)
  const [settled, setSettled] = useState(false)

  useIsomorphicLayoutEffect(() => {
    if (!editorMounted || settled) return
    // Hold the placeholder's height so content below the document doesn't
    // jump while the editor lays out behind the overlay.
    if (containerRef.current && placeholderRef.current) {
      containerRef.current.style.minHeight = `${placeholderRef.current.offsetHeight}px`
    }
    let raf2 = 0
    const raf1 = requestAnimationFrame(() => {
      raf2 = requestAnimationFrame(() => {
        if (containerRef.current) containerRef.current.style.minHeight = ''
        setSettled(true)
      })
    })
    return () => {
      cancelAnimationFrame(raf1)
      if (raf2) cancelAnimationFrame(raf2)
    }
  }, [editorMounted, settled])

  if (!ssrContentHTML) return <>{children}</>

  return (
    <div ref={containerRef} className="relative">
      {children}
      {!settled && (
        <div
          ref={placeholderRef}
          className={editorMounted ? 'dark:bg-background absolute inset-x-0 top-0 z-10 bg-white' : undefined}
          dangerouslySetInnerHTML={{__html: ssrContentHTML}}
        />
      )}
    </div>
  )
}

/**
 * Handles discovery subscription and loading states for a profile viewed within a site context.
 * Subscribes to the profile account so it gets discovered from the network, then shows
 * appropriate loading/discovery indicators before rendering the AccountPage.
 */
/** Profile discovery can take much longer than normal entity discovery (sync, etc). */
const PROFILE_DISCOVERY_TIMEOUT_MS = 5 * 60 * 1000

function SiteProfileContent({
  siteUid,
  accountUid,
  tab,
  onEditProfile,
  headerButtons,
  onFollowClick,
  pageFooter,
}: {
  siteUid: string
  accountUid: string
  tab: ProfileTab
  onEditProfile?: () => void
  headerButtons?: ReactNode
  onFollowClick?: () => void
  pageFooter?: ReactNode
}) {
  const profileId = hmId(accountUid)
  // Subscribe to trigger background discovery/sync and track discovery state
  const profileResource = useResource(profileId, {subscribed: true, recursive: true})
  const account = useAccount(accountUid)

  // Track when we started looking so we can keep showing discovery UI
  // beyond the short global discovery timeout. Periodically refetch the
  // account query while waiting — data may arrive from sync at any point.
  const [mountedAt] = useState(() => Date.now())
  const [now, setNow] = useState(() => Date.now())
  const hasData = !!account.data
  useEffect(() => {
    if (hasData) return
    const id = setInterval(() => {
      setNow(Date.now())
      account.refetch()
    }, 5_000)
    return () => clearInterval(id)
  }, [hasData, account.refetch])
  const elapsed = now - mountedAt
  const stillLooking = !hasData && elapsed < PROFILE_DISCOVERY_TIMEOUT_MS

  if (!hasData && (account.isLoading || profileResource.isDiscovering || stillLooking)) {
    return (
      <>
        <PageDiscovery entityType="profile" />
        {pageFooter}
      </>
    )
  }

  if (account.data) {
    return (
      <>
        <AccountPage
          siteUid={siteUid}
          accountUid={accountUid}
          tab={tab}
          onEditProfile={onEditProfile}
          headerButtons={headerButtons}
          onFollowClick={onFollowClick}
        />
        {pageFooter}
      </>
    )
  }

  return (
    <>
      <PageNotFound entityType="profile" />
      {pageFooter}
    </>
  )
}
